/**
 * Level.js - o quarteirao de "Operacao Blackout".
 *
 * Tema: quadra urbana do Oriente Medio no fim de tarde. 90 x 90 m jogaveis,
 * fechados por predios e por um muro de contencao - nao existe parede invisivel.
 *
 * DESENHO DO MAPA (visto de cima, +X leste, +Z norte)
 *
 *      +----------------- predios do norte (fecham o ceu) ------------------+
 *      |            RUA NORTE  (leste-oeste, linha de tiro de 90 m)         |
 *   P  | +--------+  passagem  +--------+  passagem  +--------+             | P
 *   R  | |  C3B   |   norte    |   F1   |            |   D3   |             | R
 *   E  | +--------+            +--------+            +--------+  BECO       | E
 *   D  | | C3A laje|===passarela===                  +--------+  LESTE      | D
 *   I  | +--------+                                  | D2A laje|  + arcada  | I
 *   O  | |beco norte|        PATIO CENTRAL           +--------+   com       | O
 *   S  | +--------+       (fonte, barracas,          |   D2   |   arcos     | S
 *      | | C2 laje|        sacos de areia)           +--------+             |
 *   R  | +--------+                                  |        |             | L
 *   U  | |beco sul|                                  |        |             | E
 *   A  | +--------+   +-------+   passagem           +--------+             | S
 *      | |  C1    |   |  E1   |     sul              |  D1    |             | T
 *   P  | |armazem |   +-------+                      |garagem |             | E
 *   R  | +--------+                                  +--------+             |
 *   I  |           TRAVESSA SUL (leste-oeste)                               |
 *   N  +------------------- MURO DE CONTENCAO -----------------------------+
 *
 * TRES ROTAS PARALELAS de ponta a ponta (sul -> norte):
 *   1. Rua Principal, no oeste  - linha de tiro longa (mais de 60 m livres)
 *   2. Patio Central, no meio   - espaco aberto com cobertura de altura variada
 *   3. Beco Leste + arcada      - dois corredores estreitos lado a lado
 * Elas se cruzam em seis pontos (dois becos, tres passagens e dois interiores),
 * entao nunca vira corredor unico.
 *
 * VERTICALIDADE: escada externa do patio -> laje da C2 (4,4 m) -> passarela
 * sobre o beco norte -> laje da C3A, que domina a rua principal. Do outro lado,
 * uma rampa de entulho leva a laje da D2A (3,3 m) e uma escada leva ao terraco
 * da arcada (3,8 m), que corre 50 m sobre o beco leste.
 *
 * TECNICA
 * - Tudo e construido em codigo: nenhum asset externo, nenhuma textura de disco.
 * - Toda a geometria estatica passa por um batcher que mescla por GRUPO VISUAL
 *   (material x variante de material x projeta sombra) e, DENTRO do grupo, por
 *   regiao - mas so quando a regiao sozinha passa de SPLIT_MIN_TRIS triangulos.
 *   Dividir por regiao um material que tem tres mil triangulos no mapa inteiro
 *   custa quatro malhas para economizar fill nenhum; dividir a alvenaria, que
 *   tem centenas de milhares, paga. Culling por regiao onde ele se paga, malha
 *   unica onde nao se paga.
 * - A COLISAO nao sai mais da malha desenhada. O batcher acumula em paralelo a
 *   geometria marcada como solida e monta UMA malha invisivel por material (so
 *   posicoes), que e o que vai para ctx.collision.addStatic com o surface certo.
 *   Assim solido e enfeite podem cair no MESMO draw call sem tornar solido o
 *   enfeite: antes "solido" era chave de merge e dobrava o numero de malhas.
 * - Sombra e decidida por MATERIAL (ver NEVER_CAST), nao por chamada. Quem
 *   projeta sombra e desenhado DE NOVO no shadow map, entao cada bin extra sai
 *   caro - e nao vale um bin so para o meio-fio nao projetar os 2 cm dele.
 * - As UVs sao reprojetadas em ESPACO DE MUNDO na hora do merge. Resultado: a
 *   densidade de textura e igual no mapa todo e a textura atravessa a emenda
 *   entre pecas vizinhas sem costura visivel - o oposto do "cada caixa com a
 *   textura esticada do seu jeito", que e a marca do cenario improvisado.
 * - So o que e realmente solido entra em ctx.collision.addStatic.
 */

import * as THREE from 'three';
import { buildMap, mapSpawnPoints, mapViewpoints, getMap } from './maps/index.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import * as P from './Props.js';

// ---------------------------------------------------------------------------
// materiais
// ---------------------------------------------------------------------------

/** Cor de emergencia se a MaterialLibrary ainda for stub - o mapa nunca fica preto. */
const FALLBACK = {
  concrete: [0x9c968b, 0.92, 0.0],
  asphalt: [0x3c3b3c, 0.96, 0.0],
  brick: [0x8a5a44, 0.94, 0.0],
  plaster: [0xc8b294, 0.88, 0.0],
  tile: [0xa89d8c, 0.7, 0.0],
  metal: [0x6e6a66, 0.62, 0.85],
  metal_thin: [0x7b7873, 0.55, 0.9],
  wood: [0x7a5c3c, 0.86, 0.0],
  glass: [0x1a1f22, 0.16, 0.1],
  dirt: [0x8a7457, 0.98, 0.0],
  sand: [0xc0a878, 0.97, 0.0],
  gravel: [0x8d857a, 0.95, 0.0],
  grass: [0x7d7a48, 0.95, 0.0],
  fabric: [0xb2543c, 0.94, 0.0],
  rubber: [0x232426, 0.88, 0.0],
  sandbag: [0x9a8a63, 0.97, 0.0],
  water: [0x1b2124, 0.09, 0.0]
};

/**
 * Metros de mundo por repeticao de textura - SO usado quando a MaterialLibrary
 * e stub. A fonte unica e `ctx.materials.metersPerTile(nome)`: quem desenhou a
 * textura sabe qual e o tamanho fisico da feicao que desenhou. Uma segunda
 * tabela aqui e o que fazia o tijolo sair com 29 cm de largura e a marola da
 * areia com 46 cm de periodo.
 */
const FALLBACK_TILE = {
  concrete: 1.6, asphalt: 2.2, asphalt_line: 2.2, brick: 1.32, plaster: 1.8, tile: 1.2,
  metal: 1.2, metal_thin: 2.0, wood: 0.62, glass: 1.5, dirt: 2.0,
  sand: 1.6, gravel: 1.2, grass: 1.4, fabric: 1.0, rubber: 0.9, sandbag: 0.7,
  water: 2.4
};

/**
 * Materiais DERIVADOS do Level. Sao clones dos materiais da biblioteca com
 * tinta/brilho proprios - a biblioteca nunca e mutada, e cada nome derivado e um
 * bin de merge so, entao o custo e de 1 draw call por regiao onde aparece.
 *
 * - metal_dull: aco pintado e gasto. 'metal_thin' e chapa galvanizada espelhada;
 *   luminaria de rua e corrimao de passarela saindo cromados eram o objeto mais
 *   brilhante do quadro em 07 e 08.
 * - backdrop: a cenografia do horizonte. Base clara, dessaturada e JA PUXADA
 *   para a cor do ceu, com o tile 3x maior. Com 'concrete'/'plaster' cru e tile
 *   de 1,6 m, uma caixa de 20 x 30 m sai com 12 x 19 repeticoes da mesma mancha:
 *   e o padrao de escama/acolchoado que se ve no fundo de 16 e 17.
 * - backdrop_win: o vao escuro da grade de janelas do fundo.
 */
const DERIVED = {
  metal_dull: { base: 'metal_thin', color: 0x8d8880, rough: 0.84, metal: 0.3, env: 0.5,
    tile: 2.0, surface: 'metal_thin' },
  backdrop: { base: 'concrete', color: 0xc6cad0, rough: 0.98, metal: 0.0, env: 0.6,
    tile: 5.2, surface: 'concrete' },
  backdrop_win: { base: 'concrete', color: 0x4d545c, rough: 0.74, metal: 0.02, env: 0.35,
    tile: 2.4, surface: 'concrete' }
};

/**
 * Piso de luz das superficies finas (folha e pano). O verso do plano duplicado
 * por cloth() nao recebe NADA da direta, e contra um ceu claro isso vira preto
 * absoluto - folha de palmeira e roupa de varal viravam recorte de papel no
 * contraluz. Folha e pano finos sao justamente o que TRANSLUZ no fim de tarde.
 * O termo correto seria um wrap-light no shader; aqui vai a aproximacao que nao
 * exige tocar no material da biblioteca: um piso de emissivo (some sob o sol,
 * domina no contraluz) e mais peso do environment map, que ao entardecer e
 * quente e vem do ceu inteiro.
 */
const THIN_LIFT = {
  grass: { emissive: 0x27310f, env: 1.55 },
  fabric: { emissive: 0x2c2119, env: 1.4 }
};

/**
 * Rotacao da UV projetada, em radianos, por material granular. Sem isso o
 * padrao direcional da areia corre paralelo ao eixo X em 8100 m2 e o chao le
 * como uma chapa de telha ondulada gigante. A rotacao e global e coerente,
 * entao nao cria costura entre pecas vizinhas.
 */
const UV_ROT = { sand: 0.42, gravel: -0.31, dirt: 0.23, asphalt: 0.06 };

const MAP_KEYS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap',
  'bumpMap', 'displacementMap', 'alphaMap', 'emissiveMap'];

/**
 * Adaptador de material. Le da MaterialLibrary quando ela existe, cai em um
 * material proprio quando nao existe, e garante RepeatWrapping (sem isso as UVs
 * maiores que 1 - que sao a base da nossa densidade de texel - grudam na borda).
 */
class MatLib {
  constructor(ctx) {
    this.ctx = ctx;
    this.cache = new Map();
    this.warned = false;
  }

  get(name) {
    let m = this.cache.get(name);
    if (m) return m;
    if (name === 'leaf') {
      m = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.92,
        metalness: 0,
        side: THREE.DoubleSide,
        vertexColors: true
      });
      m.name = 'level_leaf';
      m.userData = { surface: name, __levelWrap: true };
      /**
       * Translucidez da folha: a luz que atravessa a lamina quando o sol esta
       * ATRAS dela. Sem isso todo contraluz vira recorte preto, que e o que mais
       * denuncia folhagem falsa.
       *
       * A ancora e <lights_fragment_end>, nao <opaque_fragment>: `outgoingLight`
       * so passa a existir DENTRO de opaque_fragment, entao injetar antes dele
       * usando essa variavel nao compila (INVALID_OPERATION: program not valid,
       * com a folhagem inteira sumindo da tela). Somando em reflectedLight.
       * indirectDiffuse o termo entra na iluminacao pela porta certa e ainda
       * passa pelo tone mapping junto com o resto.
       */
      m.onBeforeCompile = (shader) => {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <lights_fragment_end>',
          `#include <lights_fragment_end>
          {
            // vColor e vec4 nesta build do three - dot(vec4, vec3) nao existe e
            // derruba a compilacao do fragment inteiro. Sempre .rgb aqui.
            float leafLightness = clamp(dot(vColor.rgb, vec3(0.2126, 0.7152, 0.0722)), 0.0, 1.0);
            vec3 leafBacklight = vec3(0.0);
            #if ( NUM_DIR_LIGHTS > 0 )
              #pragma unroll_loop_start
              for (int i = 0; i < NUM_DIR_LIGHTS; i++) {
                float leafBack = max(dot(-normal, directionalLights[i].direction), 0.0);
                leafBacklight += directionalLights[i].color * pow(leafBack, 1.35);
              }
              #pragma unroll_loop_end
            #endif
            float leafEdgeLight = pow(1.0 - abs(dot(normal, normalize(vViewPosition))), 2.0);
            float leafTransmission = mix(0.14, 0.52, leafLightness);
            reflectedLight.indirectDiffuse += vColor.rgb * leafTransmission *
              (leafBacklight + vec3(0.025 + 0.065 * leafEdgeLight));
          }`
        );
      };
      m.customProgramCacheKey = () => 'level_leaf_backlight_v1';
      this.cache.set(name, m);
      return m;
    }
    const der = DERIVED[name];
    if (der) {
      m = this._derive(name, der);
      this.cache.set(name, m);
      return m;
    }
    try {
      const lib = this.ctx.materials;
      m = (lib && typeof lib.get === 'function') ? lib.get(name) : null;
    } catch (err) {
      m = null;
    }
    if (m && m.isMaterial && THIN_LIFT[name]) {
      const lift = THIN_LIFT[name];
      try {
        const c = m.clone();
        c.name = m.name;
        c.emissive = new THREE.Color(lift.emissive);
        c.emissiveIntensity = 1;
        c.envMapIntensity = (m.envMapIntensity || 1) * lift.env;
        c.userData = Object.assign({}, m.userData, { surface: name, __levelWrap: true });
        this.cache.set(name, c);
        return c;
      } catch (err) {
        /* clone falhou: segue com o material da biblioteca, sem o piso de luz */
      }
    }
    if (!m || !m.isMaterial) {
      const f = FALLBACK[name] || FALLBACK.concrete;
      m = new THREE.MeshStandardMaterial({ color: f[0], roughness: f[1], metalness: f[2] });
      m.name = 'level_fallback_' + name;
      if (!this.warned) {
        this.warned = true;
        console.warn('[Level] MaterialLibrary sem "' + name + '"; usando material de emergencia.');
      }
    }
    if (!m.userData.__levelWrap) {
      for (let i = 0; i < MAP_KEYS.length; i++) {
        const tex = m[MAP_KEYS[i]];
        if (tex && tex.isTexture) {
          tex.wrapS = THREE.RepeatWrapping;
          tex.wrapT = THREE.RepeatWrapping;
          tex.needsUpdate = true;
        }
      }
      m.userData.__levelWrap = true;
    }
    this.cache.set(name, m);
    return m;
  }

  /** Clone tingido de um material da biblioteca (ver DERIVED). */
  _derive(name, d) {
    let base = null;
    try {
      const lib = this.ctx.materials;
      base = (lib && typeof lib.get === 'function') ? lib.get(d.base) : null;
    } catch (err) {
      base = null;
    }
    let m;
    if (base && base.isMaterial) {
      m = base.clone();
    } else {
      const f = FALLBACK[d.base] || FALLBACK.concrete;
      m = new THREE.MeshStandardMaterial({ color: f[0], roughness: f[1], metalness: f[2] });
    }
    m.name = 'level_' + name;
    m.color.setHex(d.color);
    m.roughness = d.rough;
    m.metalness = d.metal;
    m.envMapIntensity = d.env;
    m.userData = Object.assign({}, m.userData || {}, { surface: d.surface, __levelWrap: true });
    return m;
  }

  /** Metros de mundo por repeticao. Pergunta para quem desenhou a textura. */
  tile(name) {
    const der = DERIVED[name];
    if (der) return der.tile;
    const lib = this.ctx && this.ctx.materials;
    if (lib && typeof lib.metersPerTile === 'function') {
      const v = lib.metersPerTile(name);
      if (Number.isFinite(v) && v > 0.02) return v;
    }
    return FALLBACK_TILE[name] || 1.8;
  }

  /**
   * Material com deslocamento de UV, tinta e brilho proprios. E o que impede a
   * MESMA mancha de reboco de aparecer na MESMA altura em todo predio do mapa.
   * variant 0 devolve o material base (sem clone, sem custo).
   */
  variant(name, v) {
    if (!v) return this.get(name);
    // derivado e material fino com piso de luz nao tem variante: o clone
    // tingido JA e a identidade da peca, e pedir getVariant devolveria o
    // material cru da biblioteca (sem a tinta, sem o piso de luz)
    if (DERIVED[name] || THIN_LIFT[name]) return this.get(name);
    const lib = this.ctx && this.ctx.materials;
    if (lib && typeof lib.getVariant === 'function') {
      try {
        const m = lib.getVariant(name, v * 97 + 13);
        if (m && m.isMaterial) return m;
      } catch (err) {
        /* biblioteca ainda stub: cai no material base */
      }
    }
    return this.get(name);
  }

  /** Superficie declarada pelo material (asphalt_line -> asphalt, por exemplo). */
  surfaceOf(name, material) {
    if (DERIVED[name]) return DERIVED[name].surface;
    const lib = this.ctx && this.ctx.materials;
    if (lib && typeof lib.surfaceOf === 'function' && material) {
      try {
        const s = lib.surfaceOf(material);
        if (typeof s === 'string' && s) return s;
      } catch (err) {
        /* ignora */
      }
    }
    return name;
  }
}

// ---------------------------------------------------------------------------
// batcher
// ---------------------------------------------------------------------------

const KEEP_ATTRS = ['position', 'normal', 'uv'];

/** Deixa a geometria em formato unico (nao indexada, so pos/normal/uv). */
function normalizeGeometry(geo) {
  let g = geo.index ? geo.toNonIndexed() : geo;
  const names = Object.keys(g.attributes);
  for (let i = 0; i < names.length; i++) {
    if (KEEP_ATTRS.indexOf(names[i]) < 0) g.deleteAttribute(names[i]);
  }
  if (!g.attributes.normal) g.computeVertexNormals();
  if (!g.attributes.uv) {
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2));
  }
  g.clearGroups();
  g.morphAttributes = {};
  return g;
}

/**
 * Projeta UV pela normal dominante, em espaco de mundo (triplanar assado).
 *
 * `rot` gira a UV no plano de projecao: como a rotacao e a mesma no mapa todo,
 * pecas vizinhas continuam casando, mas o padrao direcional da textura deixa de
 * correr paralelo ao grid das ruas.
 * `phU`/`phV` deslocam a fase. Sao dadas por PREDIO: sem isso, faces
 * equivalentes de predios diferentes caem na mesma regiao da textura, e a mancha
 * do descascado nao so repete, repete ALINHADA - o olho pega na hora.
 * `flip` espelha o eixo U da fachada. Espelhar (em vez de girar 90 graus)
 * embaralha a posicao das manchas SEM deitar o escorrido de chuva, que na
 * textura de reboco corre para baixo - girar a UV faria a agua escorrer de lado.
 */
function projectWorldUV(geo, tile, rot, phU, phV, flip) {
  const pos = geo.attributes.position;
  const nor = geo.attributes.normal;
  const n = pos.count;
  const uv = new Float32Array(n * 2);
  const inv = (flip === -1 ? -1 : 1) / tile;
  const invV = 1 / tile;
  const ca = rot ? Math.cos(rot) : 1;
  const sa = rot ? Math.sin(rot) : 0;
  const ou = phU || 0;
  const ov = phV || 0;
  for (let i = 0; i < n; i++) {
    const nx = Math.abs(nor.getX(i));
    const ny = Math.abs(nor.getY(i));
    const nz = Math.abs(nor.getZ(i));
    const px = pos.getX(i);
    const py = pos.getY(i);
    const pz = pos.getZ(i);
    let u;
    let v;
    if (ny >= nx && ny >= nz) { u = px; v = pz; }
    else if (nx >= nz) { u = pz; v = py; }
    else { u = px; v = py; }
    u *= inv;
    v *= invV;
    if (rot) {
      const ru = u * ca - v * sa;
      v = u * sa + v * ca;
      u = ru;
    }
    uv[i * 2] = u + ou;
    uv[i * 2 + 1] = v + ov;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

function scaleUV(geo, su, sv) {
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
  uv.needsUpdate = true;
}

/**
 * Flags de uma peca. `receive` NAO existe mais como chave: toda malha estatica
 * do cenario recebe sombra. Ela era a flag mais barata de todas (nao custa
 * passe nenhum, so um ramo no shader) e a mais caventa de bins - separava, por
 * exemplo, o fio de rua do arame farpado do mesmo material.
 *
 * `ground` marca piso: laje de chao, meio-fio, pintura de via, poeira rente ao
 * piso. Piso vai todo para um bucket unico e NUNCA projeta sombra (chao plano
 * de 8100 m2 projetando em si mesmo e a receita do shadow acne).
 */
const F_SOLID = 4;
const F_CAST = 2;
const F_GROUND = 1;

function flagsOf(opts) {
  let f = 0;
  if (!opts || opts.solid !== false) f |= F_SOLID;
  if (!opts || opts.cast !== false) f |= F_CAST;
  if (opts && opts.ground === true) f |= F_GROUND;
  return f;
}

/** Opts prontos de piso - evita criar objeto em cada uma das centenas de lajes. */
const GROUND_OPTS = { ground: true };
const GROUND_DECO = { solid: false, cast: false, ground: true };

/**
 * SOMBRA POR MATERIAL, nao por chamada.
 *
 * Quem projeta sombra e desenhado DE NOVO no shadow map: castShadow custa, na
 * pratica, um segundo draw call. Se a decisao ficar por chamada, o mesmo
 * material precisa de dois bins na mesma regiao - um que projeta e um que nao -
 * e o que se compra com isso e o meio-fio nao projetar 2 cm de sombra. Com a
 * decisao por material o bin volta a ser um.
 *
 * NEVER_CAST: material que so aparece rente a uma superficie maior (encardido,
 * poeira, pintura de via, painel de vao) ou que le como transparente. Nada aqui
 * tem volume proprio para projetar sombra, e nada aqui perde nada por nao ter.
 * CAST_OPTIONAL: material em que a CHAMADA ainda decide. Existe por causa do
 * metal fino: antena e tubo de descida precisam do risco no chao (e o que os
 * prende no cenario), mas fio de 2 cm num texel de 6 cm sai como tracejado
 * sujo - por isso makeWire pede cast:false e continua sendo obedecido.
 */
const NEVER_CAST = new Set(['glass', 'dirt', 'asphalt', 'asphalt_line', 'tile',
  'water', 'backdrop', 'backdrop_win']);
const CAST_OPTIONAL = new Set(['metal_thin']);

/**
 * Piso da divisao por regiao, em triangulos. Uma regiao so ganha malha propria
 * dentro do grupo visual quando passa disso; abaixo, todas as regioes do grupo
 * saem em UMA malha. Com o NormalPass do SSAO mais o shadow map, cada malha
 * custa de dois a tres draw calls por frame - dividir por regiao tres mil
 * triangulos de vidro e prejuizo puro.
 */
const SPLIT_MIN_TRIS = 30000;

/**
 * Material das malhas de colisao. Elas NUNCA entram na cena (nao compilam
 * programa, nao aparecem em travessia, nao custam draw call), mas THREE.Mesh
 * exige um material - e um compartilhado e melhor que um por malha.
 */
const COLLIDER_MATERIAL = new THREE.MeshBasicMaterial({ name: 'level_collider' });

/** Quantas variantes de material o Level admite por grupo visual. */
const MAX_VARIANTS = 4;

/**
 * Flags da janela cega estampada nas COSTAS dos predios do perimetro: painel
 * escuro rente a alvenaria, sem vao de verdade. Com solid:false ela nao entra na
 * colisao, e como o material e 'glass' (que esta em NEVER_CAST) a grade inteira
 * cai no bin de vidro que a regiao ja tem - ZERO draw call novo.
 */
const BLIND_OPTS = { solid: false, cast: false };

class Batcher {
  constructor(mats) {
    this.mats = mats;
    this.bins = new Map();
    this.col = new Map();      // material -> posicoes solidas (colisao)
    this.colliders = 0;
    this.region = 'centro';
    this.variant = 0;      // indice 0..MAX_VARIANTS-1 (0 = material base)
    this.phaseU = 0;       // deslocamento de fase da UV projetada, em tiles
    this.phaseV = 0;
    this.flipU = 1;        // espelho do eixo U da fachada corrente
    this.tileMul = 1;      // escala de textura da fachada corrente
    this._meshes = 0;
  }

  setRegion(r) { this.region = r; }

  /**
   * Identidade visual do bloco corrente. `v` escolhe a variante de material
   * (tinta/brilho) e `seed` gera a pele da UV - fase, espelho e escala. A pele
   * e assada na geometria, entao NAO custa bin nem draw call: e o unico jeito
   * barato de duas fachadas do mesmo material nao sairem com a mancha do reboco
   * na mesma posicao e no mesmo tamanho.
   */
  setVariant(v, seed) {
    this.variant = ((v | 0) % MAX_VARIANTS + MAX_VARIANTS) % MAX_VARIANTS;
    this._skin((seed === undefined ? v : seed) | 0);
  }

  /**
   * Pele de UV SEM trocar a variante de material. Para material derivado (que
   * nao tem variante) e para todo caso em que a quebra da grade ja vem da fase:
   * pedir variante ali criaria um bin novo - um draw call - para devolver
   * exatamente o mesmo material.
   */
  setPhase(seed) {
    this.variant = 0;
    this._skin(seed | 0);
  }

  _skin(s) {
    this.phaseU = ((Math.imul(s ^ 0x9e37, 0x85eb) >>> 8) & 1023) / 1024 * 7.0;
    this.phaseV = ((Math.imul(s ^ 0x27d4, 0xc2b2) >>> 8) & 1023) / 1024 * 5.0;
    const h = (Math.imul(s ^ 0x51ed, 0x2545dd) >>> 9) & 1023;
    this.flipU = (h & 1) ? -1 : 1;
    this.tileMul = 0.93 + (((h >>> 3) & 7) / 7) * 0.17;
  }

  clearVariant() {
    this.variant = 0;
    this.phaseU = 0;
    this.phaseV = 0;
    this.flipU = 1;
    this.tileMul = 1;
  }

  /** Decide castShadow do bin (ver NEVER_CAST / CAST_OPTIONAL). */
  _castOf(matName, flags, bucket) {
    if (bucket === 'piso' || bucket === 'fundo') return false;
    if (NEVER_CAST.has(matName)) return false;
    if ((flags & F_CAST) === 0 && CAST_OPTIONAL.has(matName)) return false;
    return true;
  }

  /** A geometria entra JA em coordenadas de mundo e passa a pertencer ao bin. */
  push(geometry, matName, flags, uvMode, uvScale) {
    const bucket = (flags & F_GROUND) !== 0 ? 'piso' : this.region;
    const cast = this._castOf(matName, flags, bucket);
    const variant = this.variant;
    const key = bucket + '|' + matName + '|' + variant + '|' + (cast ? 1 : 0);
    let bin = this.bins.get(key);
    if (!bin) {
      bin = { mat: matName, bucket, cast, variant, tris: 0, geos: [] };
      this.bins.set(key, bin);
    }
    const g = normalizeGeometry(geometry);
    if (uvMode === 'keep') {
      if (uvScale) scaleUV(g, uvScale[0], uvScale[1]);
    } else {
      projectWorldUV(g, this.mats.tile(matName) * this.tileMul, UV_ROT[matName],
        this.phaseU, this.phaseV, this.flipU);
    }
    bin.geos.push(g);
    bin.tris += g.attributes.position.count / 3;
    if ((flags & F_SOLID) !== 0) this._collide(matName, g);
  }

  /**
   * Guarda a geometria solida para a malha de colisao daquele material. Nao
   * copia nada aqui: fica com a referencia do array de posicoes (o dispose() da
   * geometria de origem libera o buffer da GPU, nao o array de JS).
   */
  _collide(matName, g) {
    let c = this.col.get(matName);
    if (!c) {
      c = { len: 0, parts: [] };
      this.col.set(matName, c);
    }
    const arr = g.attributes.position.array;
    c.parts.push(arr);
    c.len += arr.length;
  }

  /** Achata um Object3D montado pelos Props dentro dos bins. */
  addObject(obj) {
    obj.updateMatrixWorld(true);
    obj.traverse((child) => {
      if (!child.isMesh || child.userData.dynamic) return;
      const u = child.userData;
      const g = child.geometry.clone().applyMatrix4(child.matrixWorld);
      this.push(g, u.mat || 'concrete', flagsOf(u), u.uv || 'world', u.uvScale);
    });
  }

  /**
   * Fecha os bins: monta os grupos visuais, decide onde a divisao por regiao se
   * paga, emite as malhas e monta as malhas de colisao.
   */
  build(root, collision) {
    const groups = new Map();
    const bins = Array.from(this.bins.values());
    for (let i = 0; i < bins.length; i++) {
      const bin = bins[i];
      if (bin.geos.length === 0) continue;
      // a cenografia de fundo nunca se mistura com o quarteirao: o merge com um
      // bin de perto levaria a caixa da malha a 200 m e mataria o culling dela
      const key = (bin.bucket === 'fundo' ? 'F|' : 'P|') + bin.mat + '|' +
        bin.variant + '|' + (bin.cast ? 1 : 0);
      let grp = groups.get(key);
      if (!grp) {
        grp = {
          mat: bin.mat, variant: bin.variant, cast: bin.cast,
          fundo: bin.bucket === 'fundo', bins: []
        };
        groups.set(key, grp);
      }
      grp.bins.push(bin);
    }

    this._meshes = 0;
    let tris = 0;
    const list = Array.from(groups.values());
    for (let i = 0; i < list.length; i++) {
      const grp = list[i];
      const big = [];
      const small = [];
      for (let k = 0; k < grp.bins.length; k++) {
        if (grp.bins[k].tris >= SPLIT_MIN_TRIS) big.push(grp.bins[k]);
        else small.push(grp.bins[k]);
      }
      if (big.length === 0) {
        // grupo leve: uma malha so para o mapa inteiro
        tris += this._emitMerged(root, grp, grp.bins);
      } else {
        // regiao pesada ganha malha propria (culling); o resto do grupo sai junto
        for (let k = 0; k < big.length; k++) tris += this._emitMerged(root, grp, [big[k]]);
        if (small.length > 0) tris += this._emitMerged(root, grp, small);
      }
    }
    this.bins.clear();

    const colliderMeshes = this._buildColliders(collision);
    return {
      meshes: this._meshes,
      triangles: Math.round(tris),
      colliders: this.colliders,
      colliderMeshes
    };
  }

  /** Mescla os bins recebidos em uma unica malha. Devolve triangulos emitidos. */
  _emitMerged(root, grp, bins) {
    let geos;
    if (bins.length === 1) {
      geos = bins[0].geos;
    } else {
      geos = [];
      for (let i = 0; i < bins.length; i++) {
        const src = bins[i].geos;
        for (let k = 0; k < src.length; k++) geos.push(src[k]);
      }
    }
    if (geos.length === 0) return 0;
    const label = bins.length === 1 ? bins[0].bucket : (grp.fundo ? 'fundo' : 'geral');
    const geo = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
    if (!geo) {
      console.warn('[Level] merge falhou em ' + grp.mat + '; caindo para malhas soltas.');
      let loose = 0;
      for (let i = 0; i < geos.length; i++) {
        loose += geos[i].attributes.position.count / 3;
        this._emit(root, grp, geos[i], label);
      }
      return loose;
    }
    for (let i = 0; i < geos.length; i++) {
      if (geos[i] !== geo) geos[i].dispose();
    }
    this._emit(root, grp, geo, label);
    return geo.attributes.position.count / 3;
  }

  _emit(root, grp, geo, label) {
    // aoMap do three usa o canal uv1 por padrao; sem ele a oclusao vira preto
    if (geo.attributes.uv && !geo.attributes.uv1) geo.setAttribute('uv1', geo.attributes.uv);
    geo.computeBoundingSphere();
    geo.computeBoundingBox();
    const material = this.mats.variant(grp.mat, grp.variant);
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = 'lvl_' + label + '_' + grp.mat + (grp.variant ? '_v' + grp.variant : '');
    mesh.userData.surface = this.mats.surfaceOf(grp.mat, material);
    mesh.userData.static = true;
    // a colisao vem das malhas invisiveis de _buildColliders; sem isto o solido
    // entraria duas vezes na BVH
    mesh.userData.noCollision = true;
    mesh.castShadow = grp.cast;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    mesh.layers.enable(1);
    root.add(mesh);
    this._meshes++;
  }

  /**
   * Uma malha de colisao por material, so com posicoes, fora da cena.
   *
   * Fora da cena de proposito: nao e desenhada em passe nenhum (nem no principal,
   * nem no NormalPass do SSAO, nem no shadow map) e nem aparece nas travessias de
   * cena. O CollisionWorld nao exige que o objeto esteja na cena - ele chama
   * updateWorldMatrix no que recebe -, e o surface por material continua correto,
   * que e do que a balistica e o audio dependem.
   */
  _buildColliders(collision) {
    const out = [];
    const names = Array.from(this.col.keys());
    for (let i = 0; i < names.length; i++) {
      const matName = names[i];
      const c = this.col.get(matName);
      if (!c || c.len < 9) continue;
      const arr = new Float32Array(c.len);
      let head = 0;
      for (let k = 0; k < c.parts.length; k++) {
        arr.set(c.parts[k], head);
        head += c.parts[k].length;
      }
      c.parts.length = 0;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
      geo.computeBoundingBox();
      geo.computeBoundingSphere();
      const mesh = new THREE.Mesh(geo, COLLIDER_MATERIAL);
      mesh.name = 'col_' + matName;
      mesh.userData.surface = this.mats.surfaceOf(matName, this.mats.get(matName));
      mesh.userData.static = true;
      mesh.visible = false;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      out.push(mesh);
      if (collision && typeof collision.addStatic === 'function') {
        try {
          collision.addStatic(mesh);
          this.colliders++;
        } catch (err) {
          /* colisao ainda stub */
        }
      }
    }
    this.col.clear();
    return out;
  }
}

/**
 * Recorta um retangulo (u,v) tirando os furos. Varredura por colunas: garante
 * que janelas de pavimentos diferentes na MESMA coluna sejam todas abertas.
 */
function splitRects(u0, u1, v0, v1, holes) {
  const out = [];
  const hs = [];
  for (let i = 0; i < holes.length; i++) {
    const h = holes[i];
    const a = Math.max(h.u0, u0);
    const b = Math.min(h.u1, u1);
    const c = Math.max(h.v0, v0);
    const d = Math.min(h.v1, v1);
    if (b - a > 1e-4 && d - c > 1e-4) hs.push({ u0: a, u1: b, v0: c, v1: d });
  }
  if (hs.length === 0) return [{ u0, u1, v0, v1 }];
  const cuts = [u0, u1];
  for (let i = 0; i < hs.length; i++) { cuts.push(hs[i].u0); cuts.push(hs[i].u1); }
  cuts.sort((a, b) => a - b);
  for (let i = 0; i < cuts.length - 1; i++) {
    const a = cuts[i];
    const b = cuts[i + 1];
    if (b - a < 1e-3) continue;
    const mid = (a + b) * 0.5;
    const slice = [];
    for (let k = 0; k < hs.length; k++) {
      if (hs[k].u0 <= mid && hs[k].u1 >= mid) slice.push(hs[k]);
    }
    slice.sort((p, q) => p.v0 - q.v0);
    let cv = v0;
    for (let k = 0; k < slice.length; k++) {
      if (slice[k].v0 > cv) out.push({ u0: a, u1: b, v0: cv, v1: slice[k].v0 });
      if (slice[k].v1 > cv) cv = slice[k].v1;
    }
    if (cv < v1) out.push({ u0: a, u1: b, v0: cv, v1 });
  }
  return out;
}

// ---------------------------------------------------------------------------
// coordenadas do quarteirao
// ---------------------------------------------------------------------------

const PLAY = 45;      // meia largura jogavel -> 90 x 90 m
const OUT = 58;       // ate onde vai a massa dos predios que fecham o mapa

// rua principal (norte-sul, no oeste)
const RUA_W = -45, RUA_A0 = -42.4, RUA_A1 = -33.6, RUA_E = -31;
// rua norte (leste-oeste)
const RN_S = 31, RN_A0 = 34.6, RN_A1 = 42.4, RN_N = 45;
// travessa sul (leste-oeste)
const TS_N = -32, TS_A1 = -34.6, TS_A0 = -42.4, TS_S = -45;
// beco leste e arcada
const BEC_W = 34, ARC_W = 41, ARC_E = 45, ARC_Z0 = -30, ARC_Z1 = 22, ARC_Y = 3.4, ARC_TOP = 3.85;

// alturas de piso
const Y_ASP = 0.03, Y_CALC = 0.16, Y_SAND = 0.06, Y_GRAV = 0.04;
const Y_ARMAZEM = 0.2, Y_GARAGEM = 0.12;

// lajes acessiveis
const H_C2 = 4.4, H_C3A = 4.4, H_D2A = 3.3;

// quantos exaustores continuam animados (cada um custa 1 draw call)
const MAX_LIVE_FANS = 4;

/**
 * Receitas de laje. A ordem importa: os temas sao consumidos em rodizio na
 * ordem das chamadas de _roofClutter, entao lajes vizinhas caem em receitas
 * diferentes. 'vazia' aparece uma vez a cada cinco - laje limpa ao lado de laje
 * cheia e o que da contraste; 20 lajes cheias iguais e o que le como formula.
 */
const ROOF_THEMES = ['tanques', 'antenas', 'obra', 'vazia', 'mista'];

const _tmpBox = new THREE.Box3();

export class Level {
  constructor(ctx) {
    this.ctx = ctx;
    /**
     * Qual mapa construir. 'setor7' e o quarteirao que mora NESTE arquivo (o
     * caminho original, intocado); os outros vem de src/world/maps/, montados
     * com os mesmos ajudantes desta classe. Quem escolhe e o menu ou o lobby,
     * gravando em settings.mapId antes do boot.
     */
    this.mapId = String(ctx?.mapId || ctx?.settings?.mapId || 'setor7');
    this.name = 'Quarteirao Al-Nasr';
    this.root = null;
    this.mats = null;
    this.batch = null;

    this.spawnPoints = { player: [], enemy: [] };
    this.viewpoints = [];
    this.bounds = new THREE.Box3(
      new THREE.Vector3(-PLAY, -1, -PLAY),
      new THREE.Vector3(PLAY, 14, PLAY)
    );
    this.fullBounds = new THREE.Box3(
      new THREE.Vector3(-OUT, -2, -OUT),
      new THREE.Vector3(OUT, 22, OUT)
    );
    this.stats = { meshes: 0, triangles: 0, colliders: 0 };

    this._ground = [];      // retangulos de piso: usados por _groundY
    this._colliders = [];   // malhas invisiveis registradas na colisao
    this._roofIdx = 0;      // rodizio de temas de laje (ver ROOF_THEMES)
    this._fans = [];        // exaustores que giram (unicos objetos vivos daqui)
    this._fanSpeed = null;
    this._time = 0;
  }

  // -------------------------------------------------------------------------
  // ciclo de vida
  // -------------------------------------------------------------------------

  async init() {
    const ctx = this.ctx;
    this.mats = new MatLib(ctx);
    this.batch = new Batcher(this.mats);

    this.root = new THREE.Group();
    this.root.name = 'level';
    this.root.matrixAutoUpdate = false;
    this.root.updateMatrix();
    if (ctx.scene) ctx.scene.add(this.root);

    // MAPA EXTERNO ou o quarteirao deste arquivo. O desvio e so aqui: todo o
    // resto do init (lote de geometria, colisao, entulho, limites) vale para os
    // dois caminhos, e e por isso que mapa novo nao precisa reimplementar nada
    // de infraestrutura.
    const externo = this.mapId !== 'setor7' && buildMap(this.mapId, this);
    if (!externo) {
      this.mapId = 'setor7';
      this._buildGround();
      this._buildPerimeter();
      this._buildBlockC();
      this._buildBlockCenter();
      this._buildBlockD();
      this._buildArcade();
      this._buildMainStreet();
      this._buildCourtyard();
      this._buildEastAlley();
      this._buildCrossStreets();
      this._buildInteriors();
      this._buildWires();
      this._buildBackdrop();
    }

    const built = this.batch.build(this.root, ctx.collision);
    this.stats.meshes = built.meshes;
    this.stats.triangles = built.triangles;
    this.stats.colliders = built.colliders;
    this._colliders = built.colliderMeshes;
    this.batch = null;

    this._buildDebrisInstances();

    if (this.mapId === 'setor7') {
      this._makeSpawnPoints();
      this._makeViewpoints();
    } else {
      const sp = mapSpawnPoints(this.mapId, this);
      const vp = mapViewpoints(this.mapId, this);
      // Mapa que nao entrega ponto de nascimento deixaria o jogador nascer na
      // origem, provavelmente dentro de parede: cai para o conjunto do setor 7,
      // que ao menos existe, e avisa alto.
      if (sp && Array.isArray(sp.player) && sp.player.length > 0) this.spawnPoints = sp;
      else { console.warn(`[Level] mapa "${this.mapId}" sem pontos de nascimento.`); this._makeSpawnPoints(); }
      this.viewpoints = Array.isArray(vp) && vp.length > 0 ? vp : [];

      const meta = getMap(this.mapId);
      if (meta) {
        this.name = meta.name || this.name;
        if (meta.bounds) {
          this.bounds.min.set(meta.bounds.minX, -1, meta.bounds.minZ);
          this.bounds.max.set(meta.bounds.maxX, 14, meta.bounds.maxZ);
        }
      }
    }

    this._fanSpeed = new Float32Array(this._fans.length);
    for (let i = 0; i < this._fans.length; i++) this._fanSpeed[i] = 0.7 + i * 0.23;

    // O main.js nao registra o Level no laco de frame (ele nao precisa, o
    // cenario e estatico). Como queremos os exaustores girando - movimento sutil
    // e o que tira a cena do estado de maquete -, o Level se inscreve sozinho
    // pela API publica do Engine. Se falhar, o mapa simplesmente fica parado.
    try {
      if (ctx.engine && typeof ctx.engine.add === 'function') ctx.engine.add(this);
    } catch (err) {
      console.warn('[Level] nao foi possivel registrar a animacao dos exaustores.');
    }

    // fullBounds e a massa CONSTRUIDA do quarteirao; a cenografia de fundo fica
    // de fora de proposito (senao a caixa iria a 200 m e qualquer teste de
    // pertinencia baseado nela deixaria de significar alguma coisa).
    this.fullBounds.makeEmpty();
    const kids = this.root.children;
    for (let i = 0; i < kids.length; i++) {
      const o = kids[i];
      if (!o.isMesh && !o.isInstancedMesh) continue;
      if (o.name.indexOf('lvl_fundo') === 0) continue;
      _tmpBox.setFromObject(o);
      this.fullBounds.union(_tmpBox);
    }
    if (this.fullBounds.isEmpty()) this.fullBounds.copy(this.bounds);
    return this;
  }

  /** Unica animacao do cenario: exaustores de telhado. Zero alocacao por frame. */
  update(dt) {
    const fans = this._fans;
    for (let i = 0; i < fans.length; i++) {
      fans[i].rotation.y += this._fanSpeed[i] * dt;
    }
    this._time += dt;
  }

  dispose() {
    if (!this.root) return;
    this.root.traverse((o) => {
      if (o.isMesh || o.isInstancedMesh) {
        if (o.geometry) o.geometry.dispose();
      }
    });
    // as malhas de colisao nao estao na cena: nao entram na travessia acima
    for (let i = 0; i < this._colliders.length; i++) {
      const c = this._colliders[i];
      if (c && c.geometry) c.geometry.dispose();
    }
    this._colliders.length = 0;
    if (this.root.parent) this.root.parent.remove(this.root);
    this.root = null;
    this._fans.length = 0;
  }

  // -------------------------------------------------------------------------
  // primitivas de construcao
  // -------------------------------------------------------------------------

  /** Caixa alinhada aos eixos definida por dois cantos, em coordenadas de mundo. */
  _slab(mat, x0, y0, z0, x1, y1, z1, opts) {
    const w = x1 - x0;
    const h = y1 - y0;
    const d = z1 - z0;
    if (w < 1e-4 || h < 1e-4 || d < 1e-4) return;
    const g = new THREE.BoxGeometry(w, h, d);
    g.translate((x0 + x1) * 0.5, (y0 + y1) * 0.5, (z0 + z1) * 0.5);
    this.batch.push(g, mat, flagsOf(opts), 'world', null);
  }

  /**
   * Faixa horizontal em ANEL (embasamento, cornija, faixa de piso).
   * (x0,z0,x1,z1) e o retangulo EXTERNO; t e a espessura da faixa.
   * Tem que ser anel e nao laje cheia: laje cheia atravessa o predio e tapa o
   * interior - foi exatamente isso que enterrou o piso do armazem no primeiro
   * teste de raio.
   */
  _band(mat, x0, y0, z0, x1, y1, z1, t, opts) {
    if (x1 - x0 < t * 2.2 || z1 - z0 < t * 2.2) {
      this._slab(mat, x0, y0, z0, x1, y1, z1, opts);
      return;
    }
    this._slab(mat, x0, y0, z0, x1, y1, z0 + t, opts);
    this._slab(mat, x0, y0, z1 - t, x1, y1, z1, opts);
    this._slab(mat, x0, y0, z0 + t, x0 + t, y1, z1 - t, opts);
    this._slab(mat, x1 - t, y0, z0 + t, x1, y1, z1 - t, opts);
  }

  /** Posiciona um prop e joga no batcher. */
  _place(obj, x, y, z, rotY, scale) {
    obj.position.set(x, y, z);
    if (rotY) obj.rotation.y = rotY;
    if (scale && scale !== 1) obj.scale.setScalar(scale);
    this.batch.addObject(obj);
    return obj;
  }

  /** Piso: registra o retangulo para consulta de altura e emite a laje. */
  _floor(mat, x0, z0, x1, z1, top, thick) {
    const t = thick || 0.3;
    this._slab(mat, x0, top - t, z0, x1, top, z1, { cast: false });
    this._ground.push({ x0, x1, z0, z1, y: top, surface: mat });
  }

  /** Altura do piso em (x,z). Usado por spawn e por espalhamento de entulho. */
  _groundY(x, z) {
    const list = this._ground;
    for (let i = list.length - 1; i >= 0; i--) {
      const r = list[i];
      if (x >= r.x0 && x <= r.x1 && z >= r.z0 && z <= r.z1) return r.y;
    }
    return 0;
  }

  // -------------------------------------------------------------------------
  // gerador de predio
  // -------------------------------------------------------------------------

  /**
   * Constroi um predio completo: embasamento, paredes com vaos REAIS (recortadas,
   * nao pintadas), esquadrias com requadro e peitoril, faixas de piso, pilastras
   * de canto, remendos de tijolo aparente onde o reboco caiu, cornija, laje e
   * platibanda com falhas. Devolve a altura do topo da laje.
   */
  _building(c) {
    const b = this.batch;
    b.setRegion(c.region || 'centro');
    const seed = c.seed || 11;
    // identidade da alvenaria deste predio: tinta/brilho proprios + fase de UV
    // propria. Vale so para o PANO de parede e para o tijolo aparente - trim,
    // esquadria e platibanda ficam no material base, senao os bins explodem.
    const skin = c.variant === undefined ? (seed % MAX_VARIANTS) : c.variant;
    const rng = P.makeRng(seed);
    const t = c.wallT || 0.4;
    const wall = c.wall || 'plaster';
    const trim = c.trim || 'concrete';
    const h = c.h;
    const floors = c.floors || Math.max(1, Math.round(h / 3.4));
    const fh = h / floors;
    const faces = c.faces || ['s', 'n', 'w', 'e'];
    const yBot = -0.35;

    const sides = [
      { id: 's', axis: 'x', f0: c.z0, f1: c.z0 + t, u0: c.x0, u1: c.x1, face: c.z0, rotY: Math.PI },
      { id: 'n', axis: 'x', f0: c.z1 - t, f1: c.z1, u0: c.x0, u1: c.x1, face: c.z1, rotY: 0 },
      { id: 'w', axis: 'z', f0: c.x0, f1: c.x0 + t, u0: c.z0, u1: c.z1, face: c.x0, rotY: -Math.PI / 2 },
      { id: 'e', axis: 'z', f0: c.x1 - t, f1: c.x1, u0: c.z0, u1: c.z1, face: c.x1, rotY: Math.PI / 2 }
    ];

    for (let s = 0; s < sides.length; s++) {
      const side = sides[s];
      const detailed = faces.indexOf(side.id) >= 0;
      const holes = [];
      const furn = [];

      if (detailed && c.doors) {
        for (let i = 0; i < c.doors.length; i++) {
          const d = c.doors[i];
          if (d.side !== side.id) continue;
          holes.push({ u0: d.u - d.w * 0.5, u1: d.u + d.w * 0.5, v0: -0.2, v1: d.h });
          furn.push({ kind: 'door', def: d, u: d.u, v: 0 });
        }
      }

      if (detailed) {
        const ww = c.winW || 1.15;
        const wh = c.winH || 1.5;
        const margin = c.winMargin || 1.7;
        /**
         * O EIXO e o PASSO da esquadria variam por fachada.
         *
         * Com passo fixo de 3,1 m em todo o mapa, fachadas vizinhas saem com a
         * janela na mesma coluna e na mesma altura: o quarteirao le como um
         * modulo copiado, que e a metade "composicao" do achado 3. Aqui o passo
         * balanca +-11% e o eixo desloca ate um quarto do vao - o suficiente
         * para nenhuma coluna de janela de dois predios coincidir.
         */
        const spacing = (c.winSpacing || 3.1) * (0.9 + rng() * 0.22);
        const shift = (rng() - 0.5) * 0.5;
        const L = side.u1 - side.u0;
        const usable = L - margin * 2;
        for (let f = 0; f < floors; f++) {
          const base = f * fh;
          const cy = base + (c.sill || 0.98) + wh * 0.5;
          if (cy + wh * 0.5 > h - 0.55) continue;
          if (usable < ww + 0.4) continue;
          const n = Math.max(1, Math.floor(usable / spacing));
          const step = usable / n;
          for (let i = 0; i < n; i++) {
            let uc = side.u0 + margin + step * (i + 0.5) + shift * step;
            if (uc - ww * 0.5 < side.u0 + 0.7) uc = side.u0 + 0.7 + ww * 0.5;
            if (uc + ww * 0.5 > side.u1 - 0.7) uc = side.u1 - 0.7 - ww * 0.5;
            const h0 = uc - ww * 0.5 - 0.35;
            const h1 = uc + ww * 0.5 + 0.35;
            let clash = false;
            for (let k = 0; k < holes.length; k++) {
              if (h1 > holes[k].u0 && h0 < holes[k].u1 &&
                  cy + wh * 0.5 > holes[k].v0 && cy - wh * 0.5 < holes[k].v1) clash = true;
            }
            if (clash) continue;
            holes.push({ u0: uc - ww * 0.5, u1: uc + ww * 0.5, v0: cy - wh * 0.5, v1: cy + wh * 0.5 });
            const r = rng();
            let style = 'dark';
            if (r > 0.82) style = 'glass';
            else if (r > 0.62) style = 'shutter';
            else if (r > 0.44) style = 'boarded';
            furn.push({
              kind: 'win', u: uc, v: cy, style,
              bars: f === 0 && rng() > 0.55,
              drop: 0.35 + rng() * 0.5
            });
          }
        }
      }

      // painel da parede, ja recortado
      b.setVariant(skin, seed * 31 + s);
      const rects = splitRects(side.u0, side.u1, yBot, h, holes);
      for (let i = 0; i < rects.length; i++) {
        const r = rects[i];
        if (side.axis === 'x') this._slab(wall, r.u0, r.v0, side.f0, r.u1, r.v1, side.f1);
        else this._slab(wall, side.f0, r.v0, r.u0, side.f1, r.v1, r.u1);
      }
      b.clearVariant();

      // esquadrias
      for (let i = 0; i < furn.length; i++) {
        const it = furn[i];
        let g;
        if (it.kind === 'door') {
          const d = it.def;
          if (d.style === 'roll') {
            g = P.makeRollUpDoor(this.mats, { w: d.w, h: d.h, open: d.open !== undefined ? d.open : 0.5 });
          } else {
            g = P.makeDoorUnit(this.mats, {
              w: d.w, h: d.h, style: d.style || 'wood',
              ajar: d.ajar !== undefined ? d.ajar : 0.5, open: !!d.open
            });
          }
        } else {
          g = P.makeWindowUnit(this.mats, {
            w: c.winW || 1.15, h: c.winH || 1.5, wallT: t, trim,
            style: it.style, bars: it.bars, drop: it.drop, open: !!c.openWindows
          });
        }
        if (side.axis === 'x') this._place(g, it.u, it.v, side.face, side.rotY);
        else this._place(g, side.face, it.v, it.u, side.rotY);
      }

      // remendo de tijolo aparente (reboco descascado)
      if (detailed && wall === 'plaster') {
        b.setVariant(skin, seed * 17 + s * 5);
        const np = 1 + Math.floor(rng() * 3);
        for (let i = 0; i < np; i++) {
          const pw = 1.1 + rng() * 2.6;
          const ph = 0.8 + rng() * 2.2;
          const uc = side.u0 + 1.0 + rng() * Math.max(0.2, (side.u1 - side.u0) - pw - 2.0);
          const vc = 0.4 + rng() * Math.max(0.3, h - ph - 1.4);
          for (let k = 0; k < 3; k++) {
            const sw = pw * (0.45 + rng() * 0.5);
            const sh = ph * (0.45 + rng() * 0.5);
            const ox = uc + rng() * (pw - sw);
            const oy = vc + rng() * (ph - sh);
            if (side.axis === 'x') {
              const zz = side.id === 's' ? side.face - 0.02 : side.face - 0.06;
              this._slab('brick', ox, oy, zz, ox + sw, oy + sh, zz + 0.08, { solid: false });
            } else {
              const xx = side.id === 'w' ? side.face - 0.02 : side.face - 0.06;
              this._slab('brick', xx, oy, ox, xx + 0.08, oy + sh, ox + sw, { solid: false });
            }
          }
        }
        b.clearVariant();
      }

      // Encardido do pe da parede. A textura repetida NAO consegue concentrar
      // sujeira no rodape (a rampa vertical nao fecha na emenda), entao o
      // acumulo tem que ser GEOMETRIA. A faixa e quebrada em trechos de altura
      // diferente: topo reto le como pintura, topo irregular le como acumulo.
      if (detailed && c.grime !== false) {
        const po = (c.plinth === false ? 0 : 0.12);
        const out = po + 0.022;
        const L = side.u1 - side.u0;
        const nSeg = Math.max(2, Math.round(L / 2.6));
        const step = L / nSeg;
        const sgn = (side.id === 's' || side.id === 'w') ? -1 : 1;
        for (let k = 0; k < nSeg; k++) {
          const a = side.u0 + step * k + 0.04;
          const bb = a + step - 0.08;
          const gh = 0.16 + rng() * 0.34;
          const f0 = side.face + sgn * po;
          const f1 = side.face + sgn * out;
          if (side.axis === 'x') {
            this._slab('dirt', a, -0.12, Math.min(f0, f1), bb, gh, Math.max(f0, f1),
              { solid: false, cast: false });
          } else {
            this._slab('dirt', Math.min(f0, f1), -0.12, a, Math.max(f0, f1), gh, bb,
              { solid: false, cast: false });
          }
        }
      }
    }

    // embasamento
    if (c.plinth !== false) {
      this._band(trim, c.x0 - 0.12, -0.4, c.z0 - 0.12, c.x1 + 0.12, 0.6, c.z1 + 0.12, t + 0.12);
    }
    // faixas de piso
    if (floors > 1 && c.bands !== false) {
      for (let f = 1; f < floors; f++) {
        const y = f * fh;
        this._band(trim, c.x0 - 0.09, y, c.z0 - 0.09, c.x1 + 0.09, y + 0.14, c.z1 + 0.09,
          t + 0.09, { solid: false });
      }
    }
    // pilastras de canto
    if (c.pilasters !== false) {
      const pw = 0.55;
      const corners = [[c.x0, c.z0], [c.x1 - pw, c.z0], [c.x0, c.z1 - pw], [c.x1 - pw, c.z1 - pw]];
      for (let i = 0; i < corners.length; i++) {
        this._slab(trim, corners[i][0] - 0.08, -0.2, corners[i][1] - 0.08,
          corners[i][0] + pw + 0.08, h - 0.2, corners[i][1] + pw + 0.08, { solid: false });
      }
    }
    // cornija
    if (c.cornice !== false) {
      this._band(trim, c.x0 - 0.2, h - 0.5, c.z0 - 0.2, c.x1 + 0.2, h - 0.3, c.z1 + 0.2, t + 0.2);
      this._band(trim, c.x0 - 0.13, h - 0.3, c.z0 - 0.13, c.x1 + 0.13, h - 0.15, c.z1 + 0.13,
        t + 0.13, { solid: false });
    }

    // laje de cobertura (a menos que o chamador va fazer a sua, com furos)
    if (c.roof !== false) {
      this._slab('concrete', c.x0, h - 0.3, c.z0, c.x1, h, c.z1, { cast: true });
    }

    // platibanda, com falhas onde o chamador pedir passagem
    const pp = c.parapet === undefined ? 0.95 : c.parapet;
    if (pp > 0) this._parapet(c.x0, c.z0, c.x1, c.z1, h, pp, c.gaps, trim);

    return h;
  }

  /** Platibanda com falhas (gaps) - a falha e o que deixa subir na laje. */
  _parapet(x0, z0, x1, z1, y, ph, gaps, mat) {
    const t = 0.26;
    const m = mat || 'concrete';
    const runs = [
      { id: 's', axis: 'x', f0: z0, f1: z0 + t, u0: x0, u1: x1 },
      { id: 'n', axis: 'x', f0: z1 - t, f1: z1, u0: x0, u1: x1 },
      { id: 'w', axis: 'z', f0: x0, f1: x0 + t, u0: z0, u1: z1 },
      { id: 'e', axis: 'z', f0: x1 - t, f1: x1, u0: z0, u1: z1 }
    ];
    for (let i = 0; i < runs.length; i++) {
      const r = runs[i];
      const holes = [];
      if (gaps) {
        for (let k = 0; k < gaps.length; k++) {
          if (gaps[k].side === r.id) holes.push({ u0: gaps[k].u0, u1: gaps[k].u1, v0: y - 0.1, v1: y + ph + 1 });
        }
      }
      const rects = splitRects(r.u0, r.u1, y, y + ph, holes);
      for (let k = 0; k < rects.length; k++) {
        const q = rects[k];
        if (r.axis === 'x') {
          this._slab(m, q.u0, q.v0, r.f0, q.u1, q.v1, r.f1);
          this._slab(m, q.u0 - 0.06, q.v1, r.f0 - 0.06, q.u1 + 0.06, q.v1 + 0.09, r.f1 + 0.06,
            { solid: false });
        } else {
          this._slab(m, r.f0, q.v0, q.u0, r.f1, q.v1, q.u1);
          this._slab(m, r.f0 - 0.06, q.v1, q.u0 - 0.06, r.f1 + 0.06, q.v1 + 0.09, q.u1 + 0.06,
            { solid: false });
        }
      }
    }
  }

  /**
   * Bagunca de laje POR TEMA.
   *
   * A versao anterior era uma loteria fixa de 7 pecas com probabilidades fixas,
   * chamada ~20 vezes com densidade quase igual: 20 lajes estatisticamente
   * identicas, todas com a mesma caixa d'agua, o mesmo AC e a mesma antena. Sem
   * identidade nao ha leitura de cidade, so de textura repetida em planta.
   *
   * Agora cada laje recebe uma RECEITA e os temas se alternam na ordem das
   * chamadas, de modo que lajes vizinhas nunca caem no mesmo. O tema 'vazia'
   * existe de proposito: laje limpa ao lado de laje cheia e o que cria contraste.
   */
  _roofClutter(x0, z0, x1, z1, y, seed, density, theme) {
    const rng = P.makeRng(seed);
    const cx = (x0 + x1) * 0.5;
    const cz = (z0 + z1) * 0.5;
    const w = x1 - x0;
    const d = z1 - z0;
    if (w < 1.6 || d < 1.6) return;
    const dens = density === undefined ? 1 : density;
    const area = w * d;
    const t = theme || ROOF_THEMES[this._roofIdx++ % ROOF_THEMES.length];
    const px = () => cx + (rng() - 0.5) * (w - 2.2);
    const pz = () => cz + (rng() - 0.5) * (d - 2.2);

    if (t === 'vazia') {
      this._place(P.makeChimney(this.mats, { w: 0.52 + rng() * 0.26, h: 1.2 + rng() * 1.0 }),
        px(), y, pz(), rng() * 1.5);
      if (area > 90 && rng() > 0.5) this._placeVent(px(), y, pz());
      return;
    }

    if (t === 'tanques') {
      // bateria de caixas d'agua em fila, ligadas por tubulacao rente a laje
      const n = Math.max(2, Math.min(4, Math.round(area / 55)));
      const ang = rng() * 3.1;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      const step = 1.9;
      const bx = cx - ca * step * (n - 1) * 0.5;
      const bz = cz - sa * step * (n - 1) * 0.5;
      for (let i = 0; i < n; i++) {
        const tx = bx + ca * step * i;
        const tz = bz + sa * step * i;
        this._place(P.makeWaterTank(this.mats, { r: 0.6 + rng() * 0.22, h: 1.05 + rng() * 0.45 }),
          tx, y, tz, rng() * 3.1);
        if (i > 0) {
          // tubo horizontal entre um tanque e o vizinho
          const ax = bx + ca * step * (i - 1), az = bz + sa * step * (i - 1);
          this._slab('metal_thin', Math.min(ax, tx) - 0.05, y + 0.38, Math.min(az, tz) - 0.05,
            Math.max(ax, tx) + 0.05, y + 0.48, Math.max(az, tz) + 0.05, { solid: false });
        }
      }
      this._place(P.makeAC(this.mats, { w: 0.9, h: 0.68, d: 0.7 }), px(), y + 0.34, pz(), rng() * 6.2);
      return;
    }

    if (t === 'antenas') {
      // floresta de antenas agrupada numa borda so, com cabos ate a platibanda
      const n = Math.max(3, Math.min(6, Math.round(area / 34) * dens));
      const edgeX = cx + (rng() > 0.5 ? 1 : -1) * (w * 0.5 - 1.5);
      for (let i = 0; i < n; i++) {
        const ax = edgeX + (rng() - 0.5) * 1.6;
        const az = cz + (rng() - 0.5) * (d - 2.4);
        if (rng() > 0.45) {
          this._place(P.makeAntenna(this.mats, { h: 1.5 + rng() * 1.3 }), ax, y, az, rng() * 3.1);
        } else {
          this._place(P.makeDish(this.mats, { r: 0.4 + rng() * 0.26 }), ax, y, az, rng() * 6.2);
        }
        if (i % 2 === 0) {
          this.batch.addObject(P.makeWire(this.mats, ax, y + 1.5, az,
            cx - (edgeX - cx) * 0.9, y + 0.95, az + (rng() - 0.5) * 3.0, 0.25, 0.014));
        }
      }
      this._placeVent(px(), y, pz());
      return;
    }

    if (t === 'obra') {
      // laje em obra parada: entulho, monte de areia, caixotes e vergalhao
      this._place(P.makeRubblePile(this.mats, { r: 1.0 + rng() * 0.7, h: 0.34 + rng() * 0.22,
        rng: P.makeRng(seed + 3), count: 14 }), px(), y, pz(), 0);
      this._place(P.makeRubblePile(this.mats, { mat: 'sand', r: 1.1, h: 0.4,
        rng: P.makeRng(seed + 4), count: 4 }), px(), y, pz(), 0);
      const nc = Math.max(2, Math.min(4, Math.round(area / 48)));
      for (let i = 0; i < nc; i++) {
        this._place(P.makeCrate(this.mats, { w: 0.72 + rng() * 0.3, h: 0.6 + rng() * 0.28,
          variant: rng() > 0.6 ? 1 : 0 }), px(), y, pz(), rng() * 3.1);
      }
      if (area > 70) {
        this._place(P.makeTarpStack(this.mats, { count: 2, rng: P.makeRng(seed + 5) }),
          px(), y, pz(), rng() * 3.1);
      }
      // vergalhoes saindo da laje
      for (let i = 0; i < 5; i++) {
        const rx = px(), rz = pz();
        this._slab('metal_thin', rx, y, rz, rx + 0.03, y + 0.5 + rng() * 0.6, rz + 0.03,
          { solid: false });
      }
      return;
    }

    // 'mista': a loteria antiga, agora so em uma laje a cada cinco
    const n = Math.max(2, Math.round(dens * Math.min(6, area / 42)));
    for (let i = 0; i < n; i++) {
      const qx = px();
      const qz = pz();
      const r = rng();
      if (r < 0.2) {
        this._place(P.makeWaterTank(this.mats, { r: 0.62 + rng() * 0.24, h: 1.1 + rng() * 0.4 }),
          qx, y, qz, rng() * 3.1);
      } else if (r < 0.38) {
        this._place(P.makeDish(this.mats, { r: 0.42 + rng() * 0.24 }), qx, y, qz, rng() * 6.2);
      } else if (r < 0.55) {
        this._place(P.makeAntenna(this.mats, { h: 1.6 + rng() * 1.2 }), qx, y, qz, rng() * 3.1);
      } else if (r < 0.68) {
        this._placeVent(qx, y, qz);
      } else if (r < 0.8) {
        this._place(P.makeChimney(this.mats, { w: 0.5 + rng() * 0.3, h: 1.0 + rng() * 1.1 }),
          qx, y, qz, rng() * 1.5);
      } else if (r < 0.9) {
        this._place(P.makeAC(this.mats, { w: 0.9, h: 0.68, d: 0.7 }), qx, y + 0.34, qz, rng() * 6.2);
      } else {
        this._place(P.makeCrate(this.mats, { variant: rng() > 0.5 ? 3 : 0 }), qx, y, qz, rng() * 3.1);
      }
    }
  }

  /**
   * Exaustor de telhado. Os primeiros MAX_LIVE_FANS ficam com a helice VIVA fora
   * do merge (1 draw call cada, mas movimento de verdade na silhueta); do quinto
   * em diante a helice e mesclada como geometria estatica - ninguem repara e nao
   * se paga draw call por um detalhe de 30 cm no telhado.
   */
  _placeVent(x, y, z) {
    const v = P.makeRoofVent(this.mats, {});
    v.position.set(x, y, z);
    v.updateMatrixWorld(true);
    const fan = v.userData.fan;
    if (fan && this._fans.length < MAX_LIVE_FANS) {
      fan.removeFromParent();
      fan.matrixWorld.decompose(fan.position, fan.quaternion, fan.scale);
      fan.castShadow = false;
      fan.receiveShadow = true;
      fan.userData.surface = 'metal_thin';
      this.root.add(fan);
      this._fans.push(fan);
    } else if (fan) {
      fan.userData.dynamic = false;
    }
    this.batch.addObject(v);
  }

  /**
   * Fileira de ar-condicionados numa fachada.
   *
   * `segs` sao os SEGMENTOS REAIS de parede: [{u0, u1, face, h}]. Antes este
   * metodo varria um intervalo continuo sobre um unico `face` e sorteava a
   * altura sem saber que predio existia naquele ponto - resultado: caixas
   * penduradas no ceu sobre boca de beco, sobre patio e acima da laje de predios
   * mais baixos que a altura sorteada, e ACs 0,6 m descolados da parede onde a
   * fachada e escalonada. 12 dos 36 ACs nao tinham parede atras nem embaixo.
   *
   * Agora cada AC nasce DENTRO de um segmento, a 0,8 m das bordas dele (nao
   * invade o vizinho) e so em altura que caiba abaixo da cornija daquele trecho.
   */
  _facadeAC(axis, segs, rotY, ys, seed) {
    const rng = P.makeRng(seed);
    const margin = 0.8;
    const off = 0.2;
    for (let s = 0; s < segs.length; s++) {
      const seg = segs[s];
      const span = seg.u1 - seg.u0;
      if (span < margin * 2 + 0.6) continue;
      // alturas que sobram abaixo da cornija DESTE trecho
      const ok = [];
      for (let i = 0; i < ys.length; i++) {
        if (ys[i] + 0.34 < seg.h - 0.9) ok.push(ys[i]);
      }
      if (ok.length === 0) continue;
      const n = Math.max(1, Math.floor(span / 6.8));
      for (let i = 0; i < n; i++) {
        let u = seg.u0 + ((i + 0.5) * span) / n + (rng() - 0.5) * 1.2;
        if (u < seg.u0 + margin) u = seg.u0 + margin;
        if (u > seg.u1 - margin) u = seg.u1 - margin;
        const y = ok[Math.floor(rng() * ok.length)];
        const ac = P.makeAC(this.mats, {});
        if (axis === 'x') this._place(ac, u, y, seg.face + (rotY === 0 ? off : -off), rotY);
        else this._place(ac, seg.face + (rotY === Math.PI / 2 ? off : -off), y, u, rotY);
      }
    }
  }

  /**
   * Pavimento RECUADO sobre uma laje ja pronta, com a sua propria platibanda.
   * E o recurso mais barato para quebrar a linha reta do horizonte: com todos os
   * topos planos, paralelos e na mesma cota de platibanda, quinze predios
   * diferentes leem como um degrau unico.
   */
  _setback(x0, z0, x1, z1, h, rise, wall, trim, seed) {
    const inset = 1.4;
    const a0 = x0 + inset, a1 = x1 - inset;
    const c0 = z0 + inset, c1 = z1 - inset;
    if (a1 - a0 < 2.4 || c1 - c0 < 2.4) return h;
    const top = h + rise;
    this._slab(wall || 'plaster', a0, h - 0.1, c0, a1, top - 0.3, c1);
    this._slab(trim || 'concrete', a0 - 0.14, top - 0.55, c0 - 0.14, a1 + 0.14, top - 0.35,
      c1 + 0.14, { solid: false });
    this._slab('concrete', a0, top - 0.3, c0, a1, top, c1);
    this._parapet(a0, c0, a1, c1, top, 0.75, null, trim || 'concrete');
    this._roofClutter(a0 + 1.2, c0 + 1.2, a1 - 1.2, c1 - 1.2, top, seed || 991, 0.8);
    // o anel de 1,4 m que sobrou embaixo nao pode ficar pelado
    const sd = seed || 991;
    this._place(P.makeRubblePile(this.mats, { r: 0.95, h: 0.3, rng: P.makeRng(sd + 9), count: 10 }),
      x0 + 0.7, h, (z0 + z1) * 0.5, 0);
    this._place(P.makeCrate(this.mats, { variant: 1, w: 0.72, h: 0.6, d: 0.72 }),
      x1 - 0.7, h, z0 + (z1 - z0) * 0.32, 0.6);
    this._place(P.makeChimney(this.mats, { w: 0.5, h: 1.3 }), x0 + 0.72, h,
      z1 - (z1 - z0) * 0.24, 0.3);
    return top;
  }

  /**
   * Sacada rasa de alvenaria (0,9 m de projecao) com guarda-corpo cheio.
   *
   * E o volume saliente que faltava: com toda fachada no mesmo plano, a unica
   * variacao de silhueta era a altura do predio, e o quarteirao lia como
   * elevacao desenhada. Sacada custa 6 caixas de 'concrete' no variante base -
   * ou seja, cai no bin de trim que a regiao ja tem, ZERO draw call novo.
   *
   * axis 'x': face num plano Z; axis 'z': face num plano X.
   * sgn: +1 quando a sacada avanca para a coordenada crescente.
   */
  _balcony(axis, face, sgn, u, y, w, dep) {
    const d = dep || 0.9;
    const hw = w * 0.5;
    const o = { solid: false, cast: true, receive: true };
    const f0 = face;
    const f1 = face + sgn * d;
    const lo = Math.min(f0, f1);
    const hi = Math.max(f0, f1);
    if (axis === 'x') {
      this._slab('concrete', u - hw, y, lo, u + hw, y + 0.16, hi, { cast: true });
      // pingadeira e guarda-corpo cheio
      this._slab('concrete', u - hw - 0.06, y + 0.16, lo - 0.04, u + hw + 0.06, y + 0.26,
        hi + 0.04, o);
      const gf = sgn > 0 ? hi : lo;
      this._slab('concrete', u - hw, y + 0.16, gf - 0.11, u + hw, y + 1.05, gf + 0.11, o);
      for (const s of [-1, 1]) {
        this._slab('concrete', u + s * hw - 0.055, y + 0.16, lo, u + s * hw + 0.055,
          y + 1.05, hi, o);
      }
      this._slab('concrete', u - hw - 0.07, y + 1.05, lo - 0.07, u + hw + 0.07, y + 1.16,
        hi + 0.07, o);
    } else {
      this._slab('concrete', lo, y, u - hw, hi, y + 0.16, u + hw, { cast: true });
      this._slab('concrete', lo - 0.04, y + 0.16, u - hw - 0.06, hi + 0.04, y + 0.26,
        u + hw + 0.06, o);
      const gf = sgn > 0 ? hi : lo;
      this._slab('concrete', gf - 0.11, y + 0.16, u - hw, gf + 0.11, y + 1.05, u + hw, o);
      for (const s of [-1, 1]) {
        this._slab('concrete', lo, y + 0.16, u + s * hw - 0.055, hi, y + 1.05,
          u + s * hw + 0.055, o);
      }
      this._slab('concrete', lo - 0.07, y + 1.05, u - hw - 0.07, hi + 0.07, y + 1.16,
        u + hw + 0.07, o);
    }
  }

  /** Marquise de concreto sobre porta de loja, com dois tirantes. */
  _canopy(axis, face, sgn, u, y, w, dep) {
    const d = dep || 1.25;
    const hw = w * 0.5;
    const o = { solid: false, cast: true, receive: true };
    const f0 = face;
    const f1 = face + sgn * d;
    const lo = Math.min(f0, f1);
    const hi = Math.max(f0, f1);
    if (axis === 'x') {
      this._slab('concrete', u - hw, y, lo, u + hw, y + 0.14, hi, o);
      this._slab('concrete', u - hw - 0.05, y + 0.14, lo, u + hw + 0.05, y + 0.24,
        sgn > 0 ? hi : lo + 0.12, o);
    } else {
      this._slab('concrete', lo, y, u - hw, hi, y + 0.14, u + hw, o);
      this._slab('concrete', lo, y + 0.14, u - hw - 0.05, sgn > 0 ? hi : lo + 0.12,
        y + 0.24, u + hw + 0.05, o);
    }
    // tirantes: sem eles a laje flutua, que e o mesmo defeito do toldo sem braco
    for (const s of [-1, 1]) {
      const tu = u + s * hw * 0.72;
      const tl = Math.hypot(d, 0.8);
      const ang = Math.atan2(0.8, d);
      const g = new THREE.BoxGeometry(0.035, 0.035, tl);
      // caixa e simetrica: girar por sgn*ang deixa a barra na diagonal certa
      // nos dois sentidos (a reta e a mesma, so o sinal do vetor muda)
      g.rotateX(sgn * ang);
      if (axis === 'x') {
        g.translate(tu, y + 0.46, face + sgn * d * 0.5);
      } else {
        g.rotateY(Math.PI / 2);
        g.translate(face + sgn * d * 0.5, y + 0.46, tu);
      }
      this.batch.push(g, 'metal_thin', flagsOf({ solid: false }), 'world', null);
    }
  }

  /**
   * Faixa de poeira acumulada rente as quatro paredes de um comodo. E o que
   * separa "chao de galpao" de "calcada varrida" - a textura do piso sozinha
   * nunca da isso, porque a repeticao nao sabe onde fica a parede.
   */
  _interiorDust(x0, z0, x1, z1, y, band) {
    const w = band || 1.2;
    const o = { solid: false, cast: false };
    const top = y + 0.012;
    if (x1 - x0 < w * 2.4 || z1 - z0 < w * 2.4) return;
    this._slab('dirt', x0, y - 0.14, z0, x1, top, z0 + w, o);
    this._slab('dirt', x0, y - 0.14, z1 - w, x1, top, z1, o);
    this._slab('dirt', x0, y - 0.14, z0 + w, x0 + w, top, z1 - w, o);
    this._slab('dirt', x1 - w, y - 0.14, z0 + w, x1, top, z1 - w, o);
  }

  /**
   * Posicoes numa faixa de `band` metros a partir das faces internas de um
   * retangulo. Devolve [x, z, rotY] com o rotY ja apontando para o centro do
   * comodo - carga encostada na parede fica alinhada com ela, nao torta.
   */
  _wallSpots(x0, z0, x1, z1, band, n, rng) {
    const out = [];
    const per = [x1 - x0, x1 - x0, z1 - z0, z1 - z0];
    const total = per[0] + per[1] + per[2] + per[3];
    for (let i = 0; i < n; i++) {
      let t = ((i + 0.5) / n + (rng() - 0.5) * 0.5 / n) * total;
      let side = 0;
      while (side < 3 && t > per[side]) { t -= per[side]; side++; }
      const off = 0.25 + rng() * band;
      if (side === 0) out.push([x0 + t, z0 + off, 0]);
      else if (side === 1) out.push([x0 + t, z1 - off, Math.PI]);
      else if (side === 2) out.push([x0 + off, z0 + t, Math.PI / 2]);
      else out.push([x1 - off, z0 + t, -Math.PI / 2]);
    }
    return out;
  }

  /** Converte a lista de quadras do perimetro em segmentos para _facadeAC. */
  _acSegs(list, keyA, keyB, lo, hi) {
    const out = [];
    for (let i = 0; i < list.length; i++) {
      const s = list[i];
      const u0 = Math.max(s[keyA], lo);
      const u1 = Math.min(s[keyB], hi);
      if (u1 - u0 > 2.4) out.push({ u0, u1, face: s.face, h: s.h });
    }
    return out;
  }

  /**
   * GRADE DE JANELAS CEGA numa face inteira - o detalhe mais barato que existe
   * para uma massa distante deixar de ler como caixa de papelao.
   *
   * Nao ha vao de verdade: e um painel escuro rente a alvenaria (3 cm saliente,
   * para nao brigar em Z) mais, opcionalmente, uma faixa horizontal por
   * pavimento. Cortar vao a 150 m e desperdicio; o que o olho precisa e a GRADE,
   * porque e ela que da escala - sem uma unica janela, uma caixa de 20 x 30 m
   * pode ser um predio ou uma pedra, e o cerebro escolhe pedra.
   *
   * axis 'x': a face e um plano Z (face = z), o vao corre em X.
   * axis 'z': a face e um plano X (face = x), o vao corre em Z.
   * sgn: +1 quando o lado visivel e o de coordenada crescente.
   */
  _blindWindows(c) {
    const rng = P.makeRng(c.seed);
    const stepU = c.stepU || 3.2;
    const stepV = c.stepV || 3.4;
    const ww = c.ww || 1.05;
    const wh = c.wh || 1.4;
    const axis = c.axis;
    const face = c.face;
    const sgn = c.sgn < 0 ? -1 : 1;
    const u0 = c.u0;
    const u1 = c.u1;
    const yBase = c.y0 || 0;
    const yTop = c.y1;
    const mat = c.mat || 'backdrop_win';
    const bandMat = c.band;
    const opts = c.opts || { solid: false, cast: false, receive: false };
    const span = u1 - u0;
    const hh = yTop - yBase;
    if (span < stepU + 1.2 || hh < 5.4) return;
    const cols = Math.floor((span - 1.4) / stepU);
    const rows = Math.floor((hh - 3.4) / stepV);
    if (cols < 1 || rows < 1) return;
    const marginU = (span - cols * stepU) * 0.5;
    const f0 = face - 0.03;
    const f1 = face + 0.03;
    for (let r = 0; r < rows; r++) {
      const cy = yBase + 2.5 + r * stepV;
      if (bandMat) {
        // linha de pavimento: uma peca por fileira, e o que amarra a grade
        const bf0 = face - sgn * 0.02;
        const bf1 = face + sgn * 0.07;
        if (axis === 'x') {
          this._slab(bandMat, u0 + 0.2, cy - wh * 0.5 - 0.34, Math.min(bf0, bf1),
            u1 - 0.2, cy - wh * 0.5 - 0.22, Math.max(bf0, bf1), opts);
        } else {
          this._slab(bandMat, Math.min(bf0, bf1), cy - wh * 0.5 - 0.34, u0 + 0.2,
            Math.max(bf0, bf1), cy - wh * 0.5 - 0.22, u1 - 0.2, opts);
        }
      }
      for (let c2 = 0; c2 < cols; c2++) {
        // 1 em 7 sem janela (parede cega, casa de maquinas, vao tapado) e
        // 1 em 6 mais estreita: grade perfeita le como papel milimetrado
        if (rng() < 0.15) continue;
        const w = rng() < 0.16 ? ww * 0.55 : ww;
        const uc = u0 + marginU + stepU * (c2 + 0.5);
        if (axis === 'x') {
          this._slab(mat, uc - w * 0.5, cy - wh * 0.5, f0, uc + w * 0.5, cy + wh * 0.5, f1, opts);
        } else {
          this._slab(mat, f0, cy - wh * 0.5, uc - w * 0.5, f1, cy + wh * 0.5, uc + w * 0.5, opts);
        }
      }
    }
  }

  /**
   * CENOGRAFIA DE FUNDO: um anel de massa fora do quarteirao jogavel.
   *
   * Sem isso o mundo termina no quarteirao e vira neblina: o olho nao tem para
   * onde ir depois de 90 m, e e justamente o skyline distante - torre, chamine,
   * lanca de guindaste - que da escala e separa os planos num FPS.
   *
   * O que este metodo fazia de errado (achados 1 e 2 da revisao): 26 caixas com
   * o MESMO material das paredes de perto ('concrete'/'plaster', tile de 1,6 m),
   * nenhuma janela, nenhum recuo na maioria, nada no topo e cast/receive
   * desligados - ou seja, a mesma mancha repetida 12 x 19 vezes por caixa, com
   * brilho chapado e sem nada que dissesse "isto e um predio". Agora:
   *   - material dedicado 'backdrop' (claro, dessaturado, ja puxado para a cor
   *     do horizonte, tile 5,2 m) - mata a escama e faz perspectiva aerea;
   *   - grade de janelas cega nas duas faces voltadas para o mapa;
   *   - recuo em quase metade das massas, mais caixa d'agua ou antena no topo;
   *   - o anel de dentro (ate ~112 m) projeta e recebe sombra, entao um bloco
   *     sombreia o outro e a massa ganha profundidade.
   * Tudo continua sem colisao e em poucos bins (regiao 'fundo').
   */
  _buildBackdrop() {
    const b = this.batch;
    b.setRegion('fundo');
    const rng = P.makeRng(7777);
    const far = { solid: false, cast: false, receive: false };
    const near = { solid: false, cast: true, receive: true };
    const R0 = 74;

    for (let i = 0; i < 26; i++) {
      const a = (i / 26) * Math.PI * 2 + (rng() - 0.5) * 0.2;
      const rad = R0 + rng() * 105;
      const cx = Math.cos(a) * rad;
      const cz = Math.sin(a) * rad;
      // nunca invadir a caixa jogavel nem a massa dos predios do perimetro
      if (Math.abs(cx) < OUT + 10 && Math.abs(cz) < OUT + 10) continue;
      const w = 8 + rng() * 20;
      const d = 8 + rng() * 20;
      const h = 11 + rng() * 27;
      const opts = rad < 112 ? near : far;
      const x0 = cx - w * 0.5, x1 = cx + w * 0.5;
      const z0 = cz - d * 0.5, z1 = cz + d * 0.5;
      // fase/espelho/escala de UV por massa, SEM variante de material: variante
      // em material derivado devolveria o mesmo material e cobraria um bin novo
      b.setPhase(i * 13 + 5);
      this._slab('backdrop', x0, 0, z0, x1, h, z1, opts);
      b.clearVariant();
      // platibanda: a aresta viva no alto e o que faz a massa ler como predio
      this._slab('concrete', x0 - 0.25, h, z0 - 0.25, x1 + 0.25, h + 0.9, z1 + 0.25, opts);

      // grade de janelas nas DUAS faces voltadas para o quarteirao
      this._blindWindows({ axis: 'z', face: cx > 0 ? x0 : x1, sgn: cx > 0 ? -1 : 1,
        u0: z0, u1: z1, y1: h, seed: i * 977 + 31, band: 'concrete', opts: far });
      this._blindWindows({ axis: 'x', face: cz > 0 ? z0 : z1, sgn: cz > 0 ? -1 : 1,
        u0: x0, u1: x1, y1: h, seed: i * 613 + 17, band: 'concrete', opts: far });

      // volume recuado: quase metade das massas, para o horizonte nao virar pente
      let top = h;
      if (rng() > 0.55) {
        const iw = w * 0.34, id = d * 0.34, ih = h + 4 + rng() * 8;
        b.setPhase(i * 29 + 7);
        this._slab('backdrop', cx - iw, h, cz - id, cx + iw, ih, cz + id, opts);
        b.clearVariant();
        this._slab('concrete', cx - iw - 0.2, ih, cz - id - 0.2, cx + iw + 0.2, ih + 0.6,
          cz + id + 0.2, opts);
        this._blindWindows({ axis: 'z', face: cx > 0 ? cx - iw : cx + iw,
          sgn: cx > 0 ? -1 : 1, u0: cz - id, u1: cz + id, y0: h, y1: ih,
          seed: i * 419 + 5, opts: far });
        top = ih;
      }

      /**
       * Quebra da aresta reta no topo. Sao CAIXAS, nao props: makeWaterTank e
       * makeAntenna entrariam com material solido, colisao e mais bins para um
       * detalhe a 150 m que nunca vai passar de alguns pixels.
       */
      const trick = rng();
      if (trick < 0.34) {
        // caixa d'agua sobre quatro pernas
        const tr = 1.1 + rng() * 0.7;
        const ty = top + 0.9 + (rng() > 0.5 ? 1.4 : 0);
        for (const sx of [-1, 1]) {
          for (const sz of [-1, 1]) {
            this._slab('concrete', cx + sx * tr - 0.11, top, cz + sz * tr - 0.11,
              cx + sx * tr + 0.11, ty, cz + sz * tr + 0.11, opts);
          }
        }
        this._slab('backdrop', cx - tr * 1.25, ty, cz - tr * 1.25,
          cx + tr * 1.25, ty + 1.7 + rng() * 0.9, cz + tr * 1.25, opts);
      } else if (trick < 0.62) {
        // mastro/antena, com uma travessa
        const mh = 4 + rng() * 7;
        const mx = cx + (rng() - 0.5) * w * 0.5;
        const mz = cz + (rng() - 0.5) * d * 0.5;
        this._slab('metal_thin', mx - 0.13, top, mz - 0.13, mx + 0.13, top + mh, mz + 0.13, opts);
        this._slab('metal_thin', mx - 1.1, top + mh * 0.72, mz - 0.1,
          mx + 1.1, top + mh * 0.72 + 0.14, mz + 0.1, opts);
      } else if (trick < 0.8) {
        // casa de maquinas do elevador, deslocada para um canto
        const cw = 2.2 + rng() * 2.0;
        const ox = cx + (rng() - 0.5) * (w * 0.5 - cw);
        const oz = cz + (rng() - 0.5) * (d * 0.5 - cw);
        this._slab('backdrop', ox - cw * 0.5, top, oz - cw * 0.5,
          ox + cw * 0.5, top + 2.4 + rng() * 1.4, oz + cw * 0.5, opts);
      }
    }

    // chamine industrial: a vertical mais alta do horizonte
    for (let k = 0; k < 2; k++) {
      const a = k === 0 ? -2.15 : 1.05;
      const rad = 118 + k * 34;
      const cx = Math.cos(a) * rad;
      const cz = Math.sin(a) * rad;
      const h = 44 + k * 9;
      this._slab('brick', cx - 1.5, 0, cz - 1.5, cx + 1.5, h * 0.35, cz + 1.5, far);
      this._slab('brick', cx - 1.15, h * 0.35, cz - 1.15, cx + 1.15, h, cz + 1.15, far);
      this._slab('concrete', cx - 1.5, h, cz - 1.5, cx + 1.5, h + 1.0, cz + 1.5, far);
    }

    // guindaste de obra: mastro, lanca e contra-lanca (3 caixas finas)
    const gx = -104, gz = 96;
    this._slab('metal_thin', gx - 0.9, 0, gz - 0.9, gx + 0.9, 38, gz + 0.9, far);
    this._slab('metal_thin', gx - 1.1, 38, gz - 0.7, gx + 27, 39.6, gz + 0.7, far);
    this._slab('metal_thin', gx - 11, 38.2, gz - 0.7, gx - 1.1, 39.4, gz + 0.7, far);
    this._slab('metal_thin', gx + 0.4, 39.6, gz - 0.6, gx + 1.4, 42.6, gz + 0.6, far);
    this._slab('metal_thin', gx + 16, 33.0, gz - 0.5, gx + 17, 38.0, gz + 0.5, far);

    b.setRegion('centro');
  }

  // -------------------------------------------------------------------------
  // 1. piso
  // -------------------------------------------------------------------------

  _buildGround() {
    const b = this.batch;

    // terreno base: cobre tudo, para nao existir buraco em lugar nenhum
    b.setRegion('borda');
    this._floor('dirt', -OUT - 6, -OUT - 6, OUT + 6, OUT + 6, 0, 1.2);

    // --- asfalto -----------------------------------------------------------
    b.setRegion('oeste');
    this._floor('asphalt', RUA_A0, TS_A1, RUA_A1, RN_A0, Y_ASP, 0.34);
    b.setRegion('borda');
    this._floor('asphalt', -PLAY, RN_A0, PLAY, RN_A1, Y_ASP, 0.34);
    this._floor('asphalt', -PLAY, TS_A0, PLAY, TS_A1, Y_ASP, 0.34);

    // --- calcadas (piso ceramico) e meios-fio ------------------------------
    // As faixas nao se sobrepoem: piso coplanar com piso e a receita certa para
    // z-fighting piscando na tela inteira. O meio-fio sobe 1,2 cm sobre a
    // calcada de proposito, para tambem nao ficar coplanar com ela.
    b.setRegion('oeste');
    this._floor('tile', RUA_W, TS_N, RUA_A0, RN_S, Y_CALC, 0.3);
    this._floor('tile', RUA_A1, TS_N, RUA_E, RN_S, Y_CALC, 0.3);
    this._slab('concrete', RUA_A0 - 0.22, -0.2, TS_A1, RUA_A0, Y_CALC + 0.012, RN_A0, { cast: false });
    this._slab('concrete', RUA_A1, -0.2, TS_A1, RUA_A1 + 0.22, Y_CALC + 0.012, RN_A0, { cast: false });

    b.setRegion('borda');
    this._floor('tile', -PLAY, RN_A1, PLAY, RN_N, Y_CALC, 0.3);
    this._floor('tile', -PLAY, TS_S, PLAY, TS_A0, Y_CALC, 0.3);
    this._floor('tile', RUA_W, RN_S, RUA_A0, RN_A0, Y_CALC, 0.3);
    this._floor('tile', RUA_A1, RN_S, BEC_W, RN_A0, Y_CALC, 0.3);
    this._floor('tile', ARC_W, RN_S, PLAY, RN_A0, Y_CALC, 0.3);
    this._floor('tile', RUA_W, TS_A1, RUA_A0, TS_N, Y_CALC, 0.3);
    this._floor('tile', RUA_A1, TS_A1, BEC_W, TS_N, Y_CALC, 0.3);
    this._floor('tile', ARC_W, TS_A1, PLAY, TS_N, Y_CALC, 0.3);
    this._slab('concrete', -PLAY, -0.2, RN_A1 - 0.22, PLAY, Y_CALC + 0.012, RN_A1, { cast: false });
    this._slab('concrete', -PLAY, -0.2, RN_A0, PLAY, Y_CALC + 0.012, RN_A0 + 0.22, { cast: false });
    this._slab('concrete', -PLAY, -0.2, TS_A0 - 0.22, PLAY, Y_CALC + 0.012, TS_A0, { cast: false });
    this._slab('concrete', -PLAY, -0.2, TS_A1, PLAY, Y_CALC + 0.012, TS_A1 + 0.22, { cast: false });

    // faixa central gasta e faixa de pedestre
    b.setRegion('oeste');
    for (let z = TS_A1 + 2; z < RN_A0 - 2; z += 5.6) {
      this._slab('asphalt_line', -38.12, Y_ASP, z, -37.88, Y_ASP + 0.012, z + 2.4,
        { cast: false, solid: false });
    }
    b.setRegion('borda');
    for (let i = 0; i < 7; i++) {
      const x = -42.0 + i * 1.25;
      this._slab('asphalt_line', x, Y_ASP, RN_A0 + 0.4, x + 0.55, Y_ASP + 0.012, RN_A1 - 0.4,
        { cast: false, solid: false });
    }
    for (let x = -PLAY + 4; x < PLAY - 4; x += 6.2) {
      this._slab('asphalt_line', x, Y_ASP, -38.7, x + 2.6, Y_ASP + 0.012, -38.55,
        { cast: false, solid: false });
    }

    // --- beco leste (saibro) + bocas nas travessas -------------------------
    b.setRegion('leste');
    this._floor('gravel', BEC_W, TS_N, PLAY, RN_S, Y_GRAV, 0.3);
    this._floor('gravel', BEC_W, RN_S, ARC_W, RN_A0, Y_GRAV, 0.3);
    this._floor('gravel', BEC_W, TS_A1, ARC_W, TS_N, Y_GRAV, 0.3);

    // --- patio e passagens (areia batida) ----------------------------------
    b.setRegion('centro');
    this._floor('sand', -9, -18, 15, 15, Y_SAND, 0.3);
    this._floor('sand', -9, 15, -2, RN_S, Y_SAND, 0.3);
    this._floor('sand', 9, -32, 15, -18, Y_SAND, 0.3);
    this._floor('sand', 15, 12, BEC_W, 15, Y_SAND, 0.3);
    b.setRegion('oeste');
    this._floor('gravel', RUA_E, -16, -9, -12.5, Y_GRAV, 0.3);
    this._floor('gravel', RUA_E, 8, -9, 11.5, Y_GRAV, 0.3);

    // borda de concreto entre patio e passagens (junta construtiva, quebra a chapa)
    b.setRegion('centro');
    this._slab('concrete', -9, Y_SAND - 0.16, -18, 15, Y_SAND + 0.01, -17.7, { cast: false });
    this._slab('concrete', -9, Y_SAND - 0.16, 14.7, 15, Y_SAND + 0.01, 15, { cast: false });
  }

  // -------------------------------------------------------------------------
  // 2. anel que fecha o mapa
  // -------------------------------------------------------------------------

  _buildPerimeter() {
    const b = this.batch;

    // --- oeste (fachadas da rua principal) ---------------------------------
    b.setRegion('oeste');
    const west = [
      { z0: -OUT, z1: -30, face: -45.0, h: 10.5, wall: 'plaster', seed: 101 },
      { z0: -30, z1: -8, face: -45.6, h: 13.2, wall: 'brick', seed: 102, fallen: [-22, -18.4] },
      { z0: -8, z1: 12, face: -45.0, h: 9.2, wall: 'plaster', seed: 103 },
      { z0: 12, z1: 30, face: -44.6, h: 12.2, wall: 'plaster', seed: 104, fallen: [20.5, 23.8] },
      { z0: 30, z1: OUT, face: -45.0, h: 15.5, wall: 'concrete', seed: 105 }
    ];
    for (let i = 0; i < west.length; i++) {
      const s = west[i];
      this._building({
        x0: -OUT, x1: s.face, z0: s.z0, z1: s.z1, h: s.h, wall: s.wall,
        faces: ['e'], seed: s.seed, region: 'oeste', parapet: 1.0,
        gaps: s.fallen ? [{ side: 'e', u0: s.fallen[0], u1: s.fallen[1] }] : null
      });
      this._roofClutter(s.face - 9, s.z0 + 2, s.face - 1.2, s.z1 - 2, s.h, s.seed + 7, 0.9);
      // COSTAS do quarteirao. A face externa saia laje crua: zero janela, zero
      // detalhe. Ela ocupa quase metade do quadro em 16 e aparece de toda laje
      // alta, e sem grade de janela a massa le como cenografia de papelao.
      this._blindWindows({ axis: 'z', face: -OUT, sgn: -1, u0: s.z0 + 0.9, u1: s.z1 - 0.9,
        y1: s.h - 1.0, seed: s.seed * 7 + 3, mat: 'glass', opts: BLIND_OPTS });
      // trecho de platibanda que desabou: o entulho fica na quina, sobre a laje
      if (s.fallen) {
        this._place(P.makeRubblePile(this.mats, { r: 1.5, h: 0.5,
          rng: P.makeRng(s.seed + 41), count: 18 }),
        s.face - 1.5, s.h, (s.fallen[0] + s.fallen[1]) * 0.5, 0);
      }
    }
    // ACs SOBRE A PAREDE REAL de cada trecho: a fachada oeste e escalonada
    // (-45.0 / -45.6 / -45.0 / -44.6 / -45.0) e as alturas variam de 9,2 a 15,5
    this._facadeAC('z', this._acSegs(west, 'z0', 'z1', -30, 30), Math.PI / 2,
      [3.2, 6.4, 9.1], 311);
    // Sacadas: a fachada da rua principal e um plano continuo de 60 m, e altura
    // de predio era a UNICA variacao de silhueta ali. Duas sacadas rasas jogam
    // sombra propria na parede e dao alguma coisa para o olho medir.
    this._balcony('z', -45.6, 1, -24.5, 4.0, 2.4);
    this._balcony('z', -45.0, 1, 3.5, 3.6, 2.1);
    this._balcony('z', -45.0, 1, -35.5, 4.4, 2.0);

    // --- leste -------------------------------------------------------------
    b.setRegion('leste');
    const east = [
      { z0: -OUT, z1: -26, face: 45.0, h: 12.4, wall: 'brick', seed: 111 },
      { z0: -26, z1: 2, face: 45.0, h: 9.6, wall: 'plaster', seed: 112, fallen: [-14.5, -11.0] },
      { z0: 2, z1: 30, face: 45.0, h: 11.2, wall: 'plaster', seed: 113 },
      { z0: 30, z1: OUT, face: 45.4, h: 14.2, wall: 'concrete', seed: 114 }
    ];
    for (let i = 0; i < east.length; i++) {
      const s = east[i];
      this._building({
        x0: s.face, x1: OUT, z0: s.z0, z1: s.z1, h: s.h, wall: s.wall,
        faces: ['w'], seed: s.seed, region: 'leste', parapet: 1.0,
        gaps: s.fallen ? [{ side: 'w', u0: s.fallen[0], u1: s.fallen[1] }] : null
      });
      this._roofClutter(s.face + 1.2, s.z0 + 2, s.face + 9, s.z1 - 2, s.h, s.seed + 7, 0.9);
      this._blindWindows({ axis: 'z', face: OUT, sgn: 1, u0: s.z0 + 0.9, u1: s.z1 - 0.9,
        y1: s.h - 1.0, seed: s.seed * 7 + 3, mat: 'glass', opts: BLIND_OPTS });
      if (s.fallen) {
        this._place(P.makeRubblePile(this.mats, { r: 1.5, h: 0.5,
          rng: P.makeRng(s.seed + 41), count: 18 }),
        s.face + 1.5, s.h, (s.fallen[0] + s.fallen[1]) * 0.5, 0);
      }
    }

    // --- norte -------------------------------------------------------------
    b.setRegion('borda');
    const north = [
      { x0: -PLAY, x1: -18, face: 45.0, h: 11.2, wall: 'plaster', seed: 121 },
      { x0: -18, x1: 8, face: 45.6, h: 14.6, wall: 'brick', seed: 122 },
      { x0: 8, x1: 30, face: 45.0, h: 10.2, wall: 'plaster', seed: 123 },
      { x0: 30, x1: PLAY, face: 45.0, h: 9.0, wall: 'concrete', seed: 124, tower: true }
    ];
    for (let i = 0; i < north.length; i++) {
      const s = north[i];
      this._building({
        x0: s.x0, x1: s.x1, z0: s.face, z1: OUT, h: s.h, wall: s.wall,
        faces: ['s'], seed: s.seed, region: 'borda', parapet: 1.0,
        gaps: s.gap ? [{ side: 's', u0: s.gap[0], u1: s.gap[1] }] : null
      });
      this._roofClutter(s.x0 + 2, s.face + 1.2, s.x1 - 2, s.face + 9, s.h, s.seed + 7, 0.9);
      this._blindWindows({ axis: 'x', face: OUT, sgn: 1, u0: s.x0 + 0.9, u1: s.x1 - 0.9,
        y1: s.h - 1.0, seed: s.seed * 7 + 3, mat: 'glass', opts: BLIND_OPTS });
    }
    this._facadeAC('x', this._acSegs(north, 'x0', 'x1', -30, 30), Math.PI, [3.4, 6.8], 322);

    /**
     * MARCO VERTICAL do mapa. O objeto mais alto era um predio de 15,5 m e o
     * maior prop de silhueta uma antena de 2,8 m: em 8100 m2 nao havia um unico
     * ponto para onde o olho subir, e o horizonte saia praticamente reto de
     * ponta a ponta. Esta torre escalonada de 24 m mais a caixa d'agua sobre
     * estrutura (29 m no total) se le de qualquer canto do quarteirao e da a
     * escala que o resto da cidade nao tinha.
     */
    this._building({
      x0: 32, x1: 43.4, z0: 46.6, z1: 56.4, h: 17.0, wall: 'concrete',
      faces: ['s', 'w'], seed: 125, region: 'borda', parapet: 0.9, floors: 5
    });
    this._roofClutter(33.5, 48, 42, 55, 17.0, 129, 0.7, 'vazia');
    this._building({
      x0: 34.2, x1: 41.2, z0: 48.4, z1: 54.6, h: 24.0, wall: 'concrete',
      faces: ['s', 'w'], seed: 126, region: 'borda', parapet: 0.85, floors: 7,
      winSpacing: 2.6, winW: 0.95, winH: 1.3
    });
    // caixa d'agua no topo, sobre quatro pernas de aco: a silhueta classica
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        this._slab('metal_thin', 36.4 + sx * 1.5, 24.0, 50.4 + sz * 1.4,
          36.6 + sx * 1.5, 27.2, 50.6 + sz * 1.4, { solid: false });
      }
    }
    this._slab('metal_thin', 34.7, 27.2, 48.9, 38.5, 27.4, 52.7, { solid: false });
    this._place(P.makeWaterTank(this.mats, { r: 1.05, h: 1.9, legs: false }), 36.6, 27.4, 50.8, 0.4);
    this._place(P.makeAntenna(this.mats, { h: 3.2 }), 38.2, 24.0, 53.4, 0.9);
    this._place(P.makeDish(this.mats, { r: 0.7 }), 35.2, 24.0, 49.4, -1.2);

    // --- sul: muro de contencao + telhados por tras ------------------------
    const muroH = 4.2;
    this._slab('concrete', -PLAY, -0.4, -45.9, PLAY, muroH, -45.0);
    this._slab('concrete', -PLAY - 0.1, muroH, -46.05, PLAY + 0.1, muroH + 0.18, -44.85);
    for (let x = -PLAY + 1.6; x < PLAY; x += 4.4) {
      // contrafortes na face de dentro
      this._slab('concrete', x, -0.3, -45.0, x + 0.55, muroH - 0.5, -44.42);
    }
    // arame farpado esquematico sobre o muro (baixo custo, muda a silhueta)
    for (let x = -PLAY + 2; x < PLAY - 2; x += 2.2) {
      this._slab('metal_thin', x, muroH + 0.2, -45.6, x + 0.05, muroH + 0.62, -45.55,
        { solid: false, cast: false });
      this._slab('metal_thin', x, muroH + 0.55, -45.65, x + 2.2, muroH + 0.6, -45.6,
        { solid: false, cast: false });
      this._slab('metal_thin', x, muroH + 0.34, -45.5, x + 2.2, muroH + 0.39, -45.45,
        { solid: false, cast: false });
    }
    const south = [
      { x0: -PLAY, x1: -14, h: 8.6, wall: 'plaster', seed: 131 },
      { x0: -14, x1: 12, h: 11.2, wall: 'brick', seed: 132 },
      { x0: 12, x1: PLAY, h: 7.6, wall: 'plaster', seed: 133 }
    ];
    for (let i = 0; i < south.length; i++) {
      const s = south[i];
      this._building({
        x0: s.x0, x1: s.x1, z0: -OUT, z1: -46.5, h: s.h, wall: s.wall,
        faces: ['n'], seed: s.seed, region: 'borda', parapet: 0.9, winMargin: 2.4
      });
      this._roofClutter(s.x0 + 2, -OUT + 2, s.x1 - 2, -48, s.h, s.seed + 7, 1.1);
      this._blindWindows({ axis: 'x', face: -OUT, sgn: -1, u0: s.x0 + 0.9, u1: s.x1 - 0.9,
        y1: s.h - 1.0, seed: s.seed * 7 + 3, mat: 'glass', opts: BLIND_OPTS });
    }
  }

  // -------------------------------------------------------------------------
  // 3. quadra oeste (C1 armazem, C2 laje, becos, C3)
  // -------------------------------------------------------------------------

  _buildBlockC() {
    const b = this.batch;
    b.setRegion('oeste');

    // --- C1: armazem de um pavimento alto, com interior de verdade ---------
    this._building({
      x0: RUA_E, x1: -9, z0: -32, z1: -16, h: 5.2, wall: 'plaster', floors: 1,
      faces: ['w', 's', 'n'], seed: 201, region: 'oeste', parapet: 0.75, roof: false,
      openWindows: true, sill: 3.1, winW: 1.6, winH: 1.05, winSpacing: 4.2,
      doors: [
        { side: 's', u: -22, w: 4.4, h: 3.8, style: 'roll', open: 0.55 },
        { side: 'n', u: -16.5, w: 1.2, h: 2.25, style: 'metal', ajar: 0.85, open: true },
        { side: 'w', u: -26, w: 1.2, h: 2.25, style: 'wood', ajar: 0.15, open: true }
      ]
    });
    // laje do armazem COM FUROS: e o buraco no telhado que joga a lamina de sol
    // no chao e transforma o interior numa foto, em vez de uma caixa escura.
    this._roofHoles('concrete', RUA_E, -32, -9, -16, 4.9, 5.2, [
      { u0: -25.5, u1: -22.5, v0: -27.5, v1: -25.0 },
      { u0: -17.5, u1: -15.2, v0: -23.0, v1: -21.0 },
      { u0: -28.0, u1: -26.6, v0: -20.0, v1: -18.6 }
    ]);
    // tralha so na faixa sul do telhado: mais ao norte estao os furos, e prop
    // sobre furo fica boiando no ar
    this._roofClutter(RUA_E + 2, -31.4, -11, -28.2, 5.2, 208, 1.6);
    // doca de carga na travessa: cobertura alta e boa de agachar atras
    this._slab('concrete', -26.5, -0.3, -34.2, -17.5, 1.0, -32.0);
    this._slab('concrete', -26.6, 1.0, -34.3, -17.4, 1.12, -31.9, { solid: false });
    for (let i = 0; i < 4; i++) {
      this._slab('concrete', -21.4, 0.05 + i * 0.24, -34.9 + i * 0.3, -19.6, 0.28 + i * 0.24, -34.2);
    }
    this._place(P.makeAwning(this.mats, { w: 9.5, d: 2.3, drop: 0.7 }), -22, 4.3, -32.05, Math.PI);

    // --- C2: casa do patio, laje acessivel ---------------------------------
    this._building({
      x0: RUA_E, x1: -9, z0: -12.5, z1: 8, h: H_C2, wall: 'plaster', floors: 1,
      faces: ['w', 'e', 's', 'n'], seed: 202, region: 'oeste', parapet: 1.0,
      doors: [
        { side: 'e', u: -6.5, w: 1.25, h: 2.3, style: 'wood', ajar: 0.0 },
        { side: 'w', u: -4.0, w: 1.25, h: 2.3, style: 'metal', ajar: 0.12 }
      ],
      gaps: [
        { side: 'e', u0: -1.6, u1: 1.4 },      // saida da escada externa
        { side: 'n', u0: -21.4, u1: -18.6 }    // boca da passarela
      ]
    });
    this._roofClutter(RUA_E + 2.4, -11, -11.4, 6.4, H_C2, 209, 0.75);
    // cobertura de combate na laje: sacos de areia e caixotes
    b.setRegion('centro');
    this._place(P.makeSandbagWall(this.mats, { len: 4.6, rows: 4, rng: P.makeRng(31) }),
      -13.5, H_C2, -3.2, Math.PI / 2);
    this._place(P.makeSandbagWall(this.mats, { len: 3.4, rows: 3, rng: P.makeRng(32) }),
      -16.5, H_C2, 5.4, 0);
    this._place(P.makeTarpStack(this.mats, { count: 3, rng: P.makeRng(33) }), -24, H_C2, 2.5, 0.4);
    this._place(P.makeCrate(this.mats, {}), -18.4, H_C2, -8.2, 0.7);
    this._place(P.makeCrate(this.mats, { w: 0.7, h: 0.6, d: 0.7 }), -18.1, H_C2 + 0.72, -8.0, 1.2);

    // escada externa do patio para a laje da C2
    b.setRegion('centro');
    const steps = 16;
    const rise = (H_C2 - Y_SAND) / steps;
    this._place(P.makeStairs(this.mats, { steps, rise, run: 0.42, w: 1.7, landing: 1.2 }),
      -8.15, Y_SAND, -7.0, 0);

    // --- C3A (anexo baixo, chega pela passarela) e C3B ---------------------
    b.setRegion('oeste');
    this._building({
      x0: RUA_E, x1: -9, z0: 11.5, z1: 17.5, h: H_C3A, wall: 'brick', floors: 1,
      faces: ['w', 'e', 's'], seed: 203, region: 'oeste', parapet: 1.0,
      gaps: [
        { side: 's', u0: -21.4, u1: -18.6 },   // boca da passarela
        { side: 'w', u0: 13.0, u1: 16.0 }      // seteira sobre a rua principal
      ]
    });
    this._roofClutter(RUA_E + 2, 12.6, -11.4, 16.4, H_C3A, 210, 0.6);
    this._place(P.makeSandbagWall(this.mats, { len: 3.0, rows: 3, rng: P.makeRng(34) }),
      -29.6, H_C3A, 14.5, Math.PI / 2);

    this._building({
      x0: RUA_E, x1: -9, z0: 17.5, z1: RN_S, h: 9.6, wall: 'plaster', floors: 3,
      faces: ['w', 'e', 'n'], seed: 204, region: 'oeste', parapet: 1.05,
      doors: [{ side: 'n', u: -20, w: 1.3, h: 2.4, style: 'wood', ajar: 0.0 }]
    });
    // pavimento recuado: quebra a linha reta do skyline no canto noroeste
    this._setback(RUA_E, 17.5, -9, RN_S, 9.6, 3.0, 'plaster', 'concrete', 211);
    // fachada leste da C3B (unico trecho de parede com 9,6 m nesse alinhamento)
    this._facadeAC('z', [{ u0: 18.0, u1: 30.4, face: -9, h: 9.6 }], Math.PI / 2,
      [3.6, 6.9], 333);
    // sacadas sobre a calcada da rua principal, em dois pavimentos diferentes
    this._balcony('z', RUA_E, -1, 20.4, 3.35, 2.3);
    this._balcony('z', RUA_E, -1, 26.8, 6.55, 2.0);
    // marquise sobre a porta norte da C1, na boca do beco sul
    this._canopy('x', -16, 1, -16.5, 2.62, 2.0);

    // passarela sobre o beco norte
    b.setRegion('oeste');
    this._place(P.makeCatwalk(this.mats, { len: 4.4, w: 1.4 }), -20, H_C2, 9.75, 0);
    this._slab('metal', -21.2, H_C2 - 0.3, 7.7, -18.8, H_C2, 8.3, { solid: true });
    this._slab('metal', -21.2, H_C2 - 0.3, 11.2, -18.8, H_C2, 11.8, { solid: true });
  }

  /** Laje com furos (telhado desabado do armazem). */
  _roofHoles(mat, x0, z0, x1, z1, y0, y1, holes) {
    const rects = splitRects(x0, x1, z0, z1, holes);
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      this._slab(mat, r.u0, y0, r.v0, r.u1, y1, r.v1);
    }
    // vigas expostas atravessando os furos, para o buraco nao virar recorte limpo
    for (let i = 0; i < holes.length; i++) {
      const h = holes[i];
      this._slab('wood', h.u0 - 0.3, y0 + 0.02, (h.v0 + h.v1) * 0.5 - 0.08,
        h.u1 + 0.3, y0 + 0.2, (h.v0 + h.v1) * 0.5 + 0.08, { solid: false });
      this._slab('metal_thin', (h.u0 + h.u1) * 0.5 - 0.03, y0 - 0.25, h.v0 - 0.2,
        (h.u0 + h.u1) * 0.5 + 0.03, y0 + 0.05, h.v0 + 0.35, { solid: false, cast: false });
    }
  }

  // -------------------------------------------------------------------------
  // 4. quadra central (E1 ao sul do patio, F1 ao norte)
  // -------------------------------------------------------------------------

  _buildBlockCenter() {
    const b = this.batch;
    b.setRegion('centro');

    this._building({
      x0: -9, x1: 9, z0: -32, z1: -18, h: 6.4, wall: 'plaster', floors: 2,
      faces: ['n', 's', 'e'], seed: 221, region: 'centro', parapet: 0.95,
      doors: [
        { side: 'n', u: 0.5, w: 1.3, h: 2.4, style: 'metal', ajar: 0.3 },
        { side: 's', u: -3.0, w: 3.2, h: 3.0, style: 'roll', open: 0.15 }
      ]
    });
    this._roofClutter(-8, -31, 8, -19, 6.4, 222, 1.2);
    this._place(P.makeAwning(this.mats, { w: 4.2, d: 1.7, drop: 0.6 }), 2.0, 3.5, -17.95, 0);
    this._place(P.makeSign(this.mats, { w: 3.0, h: 0.66 }), -3.0, 4.6, -17.8, 0);

    this._building({
      x0: -2, x1: 15, z0: 15, z1: RN_S, h: 8.4, wall: 'plaster', floors: 3,
      faces: ['w', 's', 'n'], seed: 223, region: 'centro', parapet: 1.0,
      doors: [
        { side: 's', u: 8.0, w: 1.3, h: 2.4, style: 'wood', ajar: 0.0 },
        { side: 'n', u: 4.0, w: 2.8, h: 2.9, style: 'roll', open: 0.2 }
      ]
    });
    this._setback(-2, 15, 15, RN_S, 8.4, 2.6, 'plaster', 'concrete', 224);
    this._place(P.makeAwning(this.mats, { w: 5.0, d: 1.8, drop: 0.62 }), 6.0, 3.4, 14.95, Math.PI);
    this._facadeAC('z', [{ u0: 15.6, u1: 30.4, face: -2, h: 8.4 }], -Math.PI / 2,
      [3.3, 6.5], 344);

    // arco sobre a passagem norte: fecha o ceu do corredor e cria uma faixa de
    // luz - detalhe barato que muda completamente a leitura da passagem
    this._slab('plaster', -9, 4.0, 22.0, -2, 8.2, 26.4);
    this._slab('concrete', -9.15, 3.82, 21.85, -1.85, 4.02, 26.55, { solid: false });
    this._slab('concrete', -9.15, 8.2, 21.85, -1.85, 8.5, 26.55, { solid: false });
    for (let i = 0; i < 3; i++) {
      const wz = 22.6 + i * 1.3;
      this._slab('glass', -6.6, 5.2, wz, -4.4, 6.6, wz + 0.12, { solid: false, cast: false });
    }
  }

  // -------------------------------------------------------------------------
  // 5. quadra leste (D1 garagem, D2, D2A laje, D3)
  // -------------------------------------------------------------------------

  _buildBlockD() {
    const b = this.batch;
    b.setRegion('leste');

    this._building({
      x0: 15, x1: BEC_W, z0: -32, z1: -18, h: 8.2, wall: 'brick', floors: 2,
      faces: ['w', 'n', 'e', 's'], seed: 241, region: 'leste', parapet: 1.0,
      sill: 4.6, winH: 1.35,
      doors: [
        { side: 'w', u: -25, w: 3.9, h: 3.5, style: 'roll', open: 0.62 },
        { side: 'e', u: -22, w: 1.25, h: 2.3, style: 'metal', ajar: 0.9, open: true },
        { side: 'n', u: 27.5, w: 1.25, h: 2.3, style: 'metal', ajar: 0.0 }
      ]
    });
    this._roofClutter(16.5, -31, 32.5, -19, 8.2, 242, 1.3);
    // janelas altas do galpao (acima do forro do interior)
    this._place(P.makeAwning(this.mats, { w: 5.4, d: 2.0, drop: 0.66 }), 15.05, 4.2, -25, -Math.PI / 2);

    /**
     * D2 sai de reboco para CONCRETO aparente, com pe-direito e esquadria
     * proprios. Uma fachada em cada tres tem que trocar de material, senao o
     * beco inteiro e a mesma parede bege com o mesmo modulo de janela (achado 3).
     * variant: 0 de proposito - com o variante base a parede cai no bin de
     * concreto que a regiao ja tem e a troca de material sai por zero draw call.
     */
    this._building({
      x0: 15, x1: BEC_W, z0: -18, z1: 4, h: 6.4, wall: 'concrete', floors: 2,
      faces: ['w', 'e'], seed: 243, variant: 0, region: 'leste', parapet: 0.95,
      sill: 1.16, winH: 1.34, winSpacing: 3.7, winW: 1.05,
      doors: [{ side: 'w', u: -8.0, w: 1.3, h: 2.4, style: 'wood', ajar: 0.0 }]
    });
    this._roofClutter(16.5, -17, 32.5, 3, 6.4, 244, 1.2);
    this._place(P.makeAwning(this.mats, { w: 4.6, d: 1.8, drop: 0.6 }), 14.95, 3.4, -12, -Math.PI / 2);

    // D2A: anexo baixo, laje alcancada pela rampa de entulho
    this._building({
      x0: 15, x1: BEC_W, z0: 4, z1: 12, h: H_D2A, wall: 'plaster', floors: 1,
      faces: ['w', 'e', 'n'], seed: 245, region: 'leste', parapet: 0.88,
      sill: 1.0, winH: 1.2,
      gaps: [{ side: 'e', u0: 5.6, u1: 9.4 }, { side: 'w', u0: 6.0, u1: 8.4 }]
    });
    this._place(P.makeSandbagWall(this.mats, { len: 3.8, rows: 3, rng: P.makeRng(41) }),
      16.6, H_D2A, 7.0, Math.PI / 2);
    this._place(P.makeWaterTank(this.mats, { r: 0.68, h: 1.15 }), 30.5, H_D2A, 5.6, 0.4);
    this._placeVent(21.0, H_D2A, 9.6);
    // rampa de entulho: parede que caiu e virou acesso
    this._place(P.makeRubbleRamp(this.mats, { len: 5.6, w: 3.4, rise: H_D2A - 0.02, rng: P.makeRng(42) }),
      39.6, Y_GRAV, 7.5, Math.PI);
    this._place(P.makeRubblePile(this.mats, { r: 1.7, h: 0.5, rng: P.makeRng(43) }), 37.0, Y_GRAV, 11.4, 0);

    this._building({
      x0: 15, x1: BEC_W, z0: 15, z1: RN_S, h: 10.6, wall: 'plaster', floors: 3,
      faces: ['s', 'n', 'e'], seed: 246, region: 'leste', parapet: 1.1,
      doors: [{ side: 'n', u: 22, w: 1.3, h: 2.4, style: 'metal', ajar: 0.0 }]
    });
    this._setback(15, 15, BEC_W, RN_S, 10.6, 3.4, 'plaster', 'concrete', 247);
    // fachada leste da quadra D, trecho a trecho: D1 (8,2), D2 (6,4),
    // D2A (3,3 - nao cabe AC nenhum) e D3 (10,6). O vao z 12..15 nao tem predio.
    this._facadeAC('z', [
      { u0: -32, u1: -18, face: BEC_W, h: 8.2 },
      { u0: -18, u1: 4, face: BEC_W, h: 6.4 },
      { u0: 4, u1: 12, face: BEC_W, h: H_D2A },
      { u0: 15, u1: 31, face: BEC_W, h: 10.6 }
    ], Math.PI / 2, [3.2, 6.4, 9.0], 355);
    for (let i = 0; i < 4; i++) {
      this._place(P.makeDownpipe(this.mats, { h: 6.2 + i * 0.7 }), BEC_W + 0.12, 0, -26 + i * 15, 0);
    }
    // sacadas sobre o beco leste + marquise na porta norte da garagem: o beco
    // tinha 60 m de parede plana dos dois lados
    this._balcony('z', BEC_W, 1, 19.0, 3.78, 2.2);
    this._balcony('z', BEC_W, 1, 26.4, 7.05, 1.9);
    this._canopy('x', -18, 1, 27.5, 2.72, 2.4);
  }

  // -------------------------------------------------------------------------
  // 6. arcada leste (colunata com arcos + terraco de tiro)
  // -------------------------------------------------------------------------

  _buildArcade() {
    const b = this.batch;
    b.setRegion('leste');

    const bays = 16;
    const span = (ARC_Z1 - ARC_Z0) / bays;
    const hw = span * 0.5;
    const r = Math.min(1.32, hw - 0.24);
    const spring = 1.9;

    // 16 vaos matematicamente identicos (mesmo raio, mesma flecha, mesmo vao)
    // sao o que mais denuncia geracao por formula. Cada arco agora sorteia raio
    // e flecha, e um a cada seis esta fechado com alvenaria de vedacao.
    const arcRng = P.makeRng(1616);
    for (let i = 0; i < bays; i++) {
      const zc = ARC_Z0 + span * (i + 0.5);
      const rr = Math.min(r * (0.9 + arcRng() * 0.16), hw - 0.2);
      const sp = spring * (0.9 + arcRng() * 0.2);
      const shape = new THREE.Shape();
      shape.moveTo(-hw, 0);
      shape.lineTo(hw, 0);
      shape.lineTo(hw, ARC_Y);
      shape.lineTo(-hw, ARC_Y);
      shape.lineTo(-hw, 0);
      const hole = new THREE.Path();
      hole.moveTo(-rr, 0);
      hole.lineTo(-rr, sp);
      hole.absarc(0, sp, rr, Math.PI, 0, true);
      hole.lineTo(rr, 0);
      hole.lineTo(-rr, 0);
      shape.holes.push(hole);
      const m = P.extrude(this.mats, 'plaster', shape, 0.5, { bevel: 0.03, curveSegments: 7 });
      m.rotation.y = Math.PI / 2;
      this._place(m, ARC_W + 0.55, 0, zc, 0);
      // pilar cheio entre os arcos, com base e capitel
      this._place(P.makeColumn(this.mats, { h: ARC_Y, r: 0.26 }), ARC_W + 0.55, 0, zc + hw, 0);
      const roll = arcRng();
      if (roll < 0.17) {
        // vao tapado com tijolo (alvenaria de vedacao), deixando uma seteira
        this._slab('brick', ARC_W + 0.36, 0, zc - rr, ARC_W + 0.74, sp + rr * 0.72, zc + rr);
        this._slab('brick', ARC_W + 0.36, sp + rr * 0.72 + 0.42, zc - rr,
          ARC_W + 0.74, sp + rr, zc + rr, { solid: false });
      } else if (roll < 0.3) {
        // vao entulhado por sacos de areia: cobertura util no corredor
        this._place(P.makeSandbagWall(this.mats, { len: rr * 1.8, rows: 3,
          rng: P.makeRng(1600 + i) }), ARC_W + 0.55, Y_GRAV, zc, Math.PI / 2);
      }
    }

    // laje do terraco sobre a arcada + apoio no fundo
    this._slab('concrete', ARC_W, ARC_Y, ARC_Z0, ARC_E, ARC_TOP, ARC_Z1);
    this._slab('concrete', ARC_W - 0.16, ARC_Y - 0.22, ARC_Z0, ARC_W + 0.05, ARC_Y, ARC_Z1,
      { solid: false });
    this._slab('plaster', ARC_W + 1.1, 0, ARC_Z0, ARC_E, ARC_Y, ARC_Z0 + 0.4);
    this._slab('plaster', ARC_W + 1.1, 0, ARC_Z1 - 0.4, ARC_E, ARC_Y, ARC_Z1);
    // platibanda do terraco com seteiras
    this._parapet(ARC_W, ARC_Z0, ARC_E, ARC_Z1, ARC_TOP, 0.92, [
      { side: 'n', u0: ARC_W + 0.6, u1: ARC_W + 2.4 },
      { side: 'w', u0: -12.0, u1: -9.5 },
      { side: 'w', u0: 6.0, u1: 8.0 }
    ], 'concrete');
    // entulho no pe dos trechos de platibanda que cairam
    this._place(P.makeRubblePile(this.mats, { r: 1.3, h: 0.42, rng: P.makeRng(1181), count: 16 }),
      ARC_W + 0.9, ARC_TOP, -10.7, 0);
    this._place(P.makeRubblePile(this.mats, { r: 1.1, h: 0.36, rng: P.makeRng(1182), count: 13 }),
      ARC_W + 0.8, ARC_TOP, 7.0, 0);
    /**
     * 52 m de corredor com zero objeto leem como passarela inacabada, e a rota
     * alta mais longa do mapa virava estande de tiro sem cobertura. Espacamento
     * de proposito IRREGULAR: 3,2 / 8 / 5 / 9 / 7 metros entre pecas.
     */
    this._place(P.makeSandbagWall(this.mats, { len: 3.2, rows: 3, rng: P.makeRng(1191) }),
      43.0, ARC_TOP, -22.0, Math.PI / 2);
    this._place(P.makeCrate(this.mats, { w: 0.9, h: 0.78, d: 0.9 }), 42.4, ARC_TOP, -14.0, 0.4);
    this._place(P.makeCrate(this.mats, { w: 0.78, h: 0.66, d: 0.78, variant: 1 }),
      42.6, ARC_TOP + 0.78, -13.85, 1.1);
    this._place(P.makeBarrel(this.mats, { mat: 'metal', variant: 2 }), 44.0, ARC_TOP, -18.6, 0.8);
    this._place(P.makeSandbagWall(this.mats, { len: 3.6, rows: 4, rng: P.makeRng(1192) }),
      42.9, ARC_TOP, -6.0, Math.PI / 2);
    this._place(P.makeCrate(this.mats, { w: 0.86, h: 0.72, d: 0.86, variant: 3 }),
      43.9, ARC_TOP, 2.4, -0.3);
    this._place(P.makeTarpStack(this.mats, { count: 2, rng: P.makeRng(1193) }),
      42.6, ARC_TOP, 3.4, 0.5);
    this._place(P.makeSandbagWall(this.mats, { len: 3.0, rows: 3, rng: P.makeRng(1194) }),
      43.2, ARC_TOP, 9.0, Math.PI / 2);
    this._place(P.makeWaterTank(this.mats, { r: 0.66, h: 1.15 }), 44.0, ARC_TOP, 13.0, 0.7);
    this._place(P.makeCrate(this.mats, { w: 0.8, h: 0.68, d: 0.8, variant: 2 }),
      42.0, ARC_TOP, 14.2, 0.9);
    for (let i = 0; i < 6; i++) {
      const z = ARC_Z0 + 5 + i * 7.6;
      this._slab('concrete', ARC_W, ARC_TOP + 0.42, z, ARC_W + 0.3, ARC_TOP + 0.95, z + 0.9,
        { solid: false });
    }

    // escada externa que da acesso ao terraco (unico caminho para cima)
    const st = 14;
    this._place(P.makeStairs(this.mats, {
      steps: st, rise: (ARC_TOP - Y_GRAV) / st, run: 0.5, w: 1.7, landing: 0.9
    }), 43.4, Y_GRAV, 29.4, Math.PI);
    this._place(P.makeRailing(this.mats, { len: 4.0, h: 1.02, mesh: true }),
      43.4, ARC_TOP, 21.6, 0);

    // vitrines sob a colunata: da vida a fachada e cria cantos de tiro
    // O mesmo trio (placa + caixote + tambor) a cada 10,5 m com os mesmos
    // offsets fazia a colunata inteira ler como um unico modulo copiado. Agora
    // a posicao balanca, algumas iteracoes sao puladas e o prop e sorteado.
    const shopRng = P.makeRng(1155);
    for (let i = 0; i < 7; i++) {
      const z = ARC_Z0 + 3 + i * 7.4 + P.rand(shopRng, -2.4, 2.4);
      if (z < ARC_Z0 + 1.5 || z > ARC_Z1 - 1.5) continue;
      if (shopRng() < 0.28) continue;
      if (shopRng() > 0.42) {
        this._place(P.makeSign(this.mats, { w: 1.8 + shopRng() * 1.4, h: 0.52 + shopRng() * 0.2 }),
          44.6, 2.55 + shopRng() * 0.4, z, Math.PI / 2);
      }
      const pick = Math.floor(shopRng() * 5);
      const pz = z + P.rand(shopRng, -1.8, 1.8);
      if (pick === 0) {
        this._place(P.makeBarrel(this.mats, { mat: 'metal', variant: shopRng() > 0.6 ? 1 : 0,
          open: shopRng() > 0.7 }), 44.2, Y_GRAV, pz, shopRng() * 3.1);
      } else if (pick === 1) {
        const cw = 0.74 + shopRng() * 0.3;
        this._place(P.makeCrate(this.mats, { w: cw, h: 0.6 + shopRng() * 0.28, d: cw,
          variant: Math.floor(shopRng() * 4) }), 43.6, Y_GRAV, pz, shopRng() * 3.1);
        if (shopRng() > 0.72) {
          this._place(P.makeCrate(this.mats, { w: cw * 0.86, h: 0.56, d: cw * 0.86, variant: 2 }),
            43.62, Y_GRAV + 0.62, pz + 0.06, shopRng() * 3.1);
        }
      } else if (pick === 2) {
        for (let k = 0; k < 3; k++) {
          this._place(P.makeTire(this.mats, {}), 43.9, Y_GRAV + k * 0.22, pz, k * 0.9);
        }
      } else if (pick === 3) {
        this._place(P.makeRubblePile(this.mats, { r: 0.9 + shopRng() * 0.6, h: 0.3,
          rng: P.makeRng(1160 + i), count: 12 }), 43.8, Y_GRAV, pz, 0);
      }
    }
  }

  // -------------------------------------------------------------------------
  // 7. rua principal (linha de tiro longa, quebrada por cobertura)
  // -------------------------------------------------------------------------

  _buildMainStreet() {
    const b = this.batch;
    b.setRegion('oeste');
    const rng = P.makeRng(501);

    // postes na calcada leste + luminarias
    this._poles = [];
    for (let i = 0; i < 6; i++) {
      const z = -36 + i * 14;
      this._place(P.makePole(this.mats, { h: 8.4, transformer: i === 2 || i === 4 }),
        -32.3, Y_CALC, z, i % 2 ? Math.PI : 0);
      this._poles.push([-32.3, Y_CALC + 8.4 - 0.55, z]);
    }

    // veiculos: um caminhao no meio da rua e dois carros estacionados
    this._place(P.makeVehicle(this.mats, { kind: 'pickup', burned: false, doorOpen: true }),
      -38.6, Y_ASP, -6.5, 0.12);
    this._place(P.makeVehicle(this.mats, { kind: 'sedan' }), -35.2, Y_ASP, 13.5, Math.PI / 2 + 0.04);
    this._place(P.makeVehicle(this.mats, { kind: 'sedan', burned: true, doorOpen: true }),
      -40.6, Y_ASP, 26.0, -Math.PI / 2 - 0.1);

    // posto de bloqueio: barreiras + sacos + tambores (cobertura media)
    for (let i = 0; i < 4; i++) {
      this._place(P.makeJerseyBarrier(this.mats, { len: 2.4 }),
        -42.0 + i * 2.5, Y_ASP, 19.6 + (i % 2) * 1.1, (i % 2) * 0.12);
    }
    this._place(P.makeSandbagWall(this.mats, { len: 4.2, rows: 4, rng: P.makeRng(52) }),
      -36.0, Y_ASP, 21.4, 0.06);
    this._place(P.makeSandbagWall(this.mats, { len: 2.6, rows: 3, rng: P.makeRng(53) }),
      -34.2, Y_ASP, 23.2, Math.PI / 2);
    this._place(P.makeBarrel(this.mats, { open: true }), -37.4, Y_ASP, 23.4, 0);
    this._place(P.makeBarrel(this.mats, {}), -38.3, Y_ASP, 24.0, 0.6);

    // cobertura baixa espalhada, para nunca faltar onde agachar
    this._place(P.makeJerseyBarrier(this.mats, { len: 2.4 }), -39.4, Y_ASP, -20.0, 0.05);
    this._place(P.makeJerseyBarrier(this.mats, { len: 2.4 }), -36.2, Y_ASP, -18.6, -0.09);
    this._place(P.makeDumpster(this.mats, {}), -32.9, Y_CALC, 3.4, Math.PI / 2 + 0.06);
    this._place(P.makeDumpster(this.mats, {}), -43.4, Y_CALC, -14.0, -Math.PI / 2);
    this._place(P.makeContainer(this.mats, { len: 6.06 }), -43.0, Y_CALC, 33.0, 0.04);
    this._place(P.makeRubblePile(this.mats, { r: 2.3, h: 0.75, rng: P.makeRng(54), count: 30 }),
      -43.2, Y_CALC, 8.5, 0);
    this._place(P.makePlanter(this.mats, { rng: P.makeRng(55) }), -32.6, Y_CALC, -28.0, 0);
    this._place(P.makePlanter(this.mats, { rng: P.makeRng(56) }), -32.6, Y_CALC, 30.0, 0);
    // palmeiras da calcada: a unica curva alta da rua, e o que quebra a leitura
    // de "corredor de caixas" na linha de tiro de 60 m
    this._place(P.makePalm(this.mats, { h: 6.4, rng: P.makeRng(811) }), -32.7, Y_CALC, -25.5, 0.4);
    this._place(P.makePalm(this.mats, { h: 5.5, rng: P.makeRng(812) }), -32.6, Y_CALC, -1.5, -1.9);
    this._place(P.makePalm(this.mats, { h: 7.1, rng: P.makeRng(813) }), -32.7, Y_CALC, 24.5, 1.3);

    // toldos e placas nas fachadas da quadra C (lado leste da rua)
    const shopZ = [-29, -22, -6, 2, 20, 27];
    for (let i = 0; i < shopZ.length; i++) {
      this._place(P.makeAwning(this.mats, { w: 3.4 + rng() * 1.6, d: 1.6, drop: 0.58 }),
        RUA_E + 0.05, 3.15, shopZ[i], -Math.PI / 2);
      this._place(P.makeSign(this.mats, { w: 2.2 + rng() * 1.0, h: 0.58 }),
        RUA_E - 0.16, 4.15, shopZ[i], -Math.PI / 2);
    }
    for (let i = 0; i < 5; i++) {
      this._place(P.makeDownpipe(this.mats, { h: 4.9 }), RUA_E - 0.12, 0, -30 + i * 14.5, 0);
    }
    // fachada oeste da quadra C. Os becos (z -16..-12,5 e z 8..11,5) ficam de
    // FORA: era exatamente ali que nasciam os ACs boiando sobre o vazio.
    this._facadeAC('z', [
      { u0: -32, u1: -16, face: RUA_E, h: 5.2 },
      { u0: -12.5, u1: 8, face: RUA_E, h: H_C2 },
      { u0: 11.5, u1: 17.5, face: RUA_E, h: H_C3A },
      { u0: 17.5, u1: 31, face: RUA_E, h: 9.6 }
    ], -Math.PI / 2, [3.0, 5.6], 366);
  }

  // -------------------------------------------------------------------------
  // 8. patio central
  // -------------------------------------------------------------------------

  _buildCourtyard() {
    const b = this.batch;
    b.setRegion('centro');
    const rng = P.makeRng(601);

    this._place(P.makeFountain(this.mats, { r: 2.1 }), 2.0, Y_SAND, -3.0, 0);
    this._place(P.makePlanter(this.mats, { w: 1.6, rng: P.makeRng(61) }), 6.4, Y_SAND, -5.6, 0.3);
    this._place(P.makePlanter(this.mats, { w: 1.6, rng: P.makeRng(62) }), -2.4, Y_SAND, -0.4, -0.3);

    // feira: tres barracas com lona, quebram a linha de visao no meio do patio
    this._place(P.makeMarketStall(this.mats, { rng: P.makeRng(71) }), -4.6, Y_SAND, 5.2, 0.18);
    this._place(P.makeMarketStall(this.mats, { w: 2.0, d: 1.4, rng: P.makeRng(72) }),
      -1.0, Y_SAND, 8.0, -0.42);
    this._place(P.makeMarketStall(this.mats, { w: 2.7, d: 1.6, rng: P.makeRng(73) }),
      3.2, Y_SAND, 5.8, 0.06);
    for (let i = 0; i < 7; i++) {
      const cw = 0.7 + rng() * 0.3;
      const cx = -6 + rng() * 11;
      const cz = 3.4 + rng() * 6.4;
      const ch = 0.55 + rng() * 0.3;
      this._place(P.makeCrate(this.mats, { w: cw, h: ch, d: 0.7,
        variant: Math.floor(rng() * 4) }), cx, Y_SAND, cz, rng() * 3.1);
      if (rng() > 0.68) {
        this._place(P.makeCrate(this.mats, { w: cw * 0.85, h: ch * 0.86, d: 0.62,
          variant: Math.floor(rng() * 3) }), cx + 0.05, Y_SAND + ch, cz - 0.04, rng() * 3.1);
      }
    }

    // muro baixo: cobertura de agachar exatamente no meio do vao aberto
    this._slab('concrete', -2.4, Y_SAND - 0.2, 0.6, 6.8, 1.02, 1.0);
    this._slab('concrete', -2.55, 1.02, 0.45, 6.95, 1.16, 1.15, { solid: false });
    this._slab('concrete', 9.4, Y_SAND - 0.2, -9.6, 10.0, 1.35, -4.0);
    this._slab('concrete', 9.25, 1.35, -9.75, 10.15, 1.49, -3.85, { solid: false });

    // contentores: cobertura alta e degrau de descida da laje da C2
    this._place(P.makeContainer(this.mats, { len: 6.06 }), 11.2, Y_SAND, 6.4, -0.22);
    this._place(P.makeContainer(this.mats, { len: 6.06, mat: 'metal' }), 12.0, Y_SAND, -12.6, 1.52);
    this._place(P.makeContainer(this.mats, { len: 6.06 }), -5.4, Y_SAND, -15.4, 0.06);
    this._place(P.makeCrate(this.mats, { w: 1.0, h: 0.85, d: 1.0 }), -6.0, Y_SAND, -12.0, 0.35);
    this._place(P.makeCrate(this.mats, { w: 0.86, h: 0.72, d: 0.86 }), -6.2, Y_SAND + 0.86, -11.9, 0.9);

    // emplacamento de sacos de areia em L, virado para o norte
    this._place(P.makeSandbagWall(this.mats, { len: 5.0, rows: 4, rng: P.makeRng(63) }),
      9.4, Y_SAND, 1.6, 0.02);
    this._place(P.makeSandbagWall(this.mats, { len: 2.8, rows: 4, rng: P.makeRng(64) }),
      11.8, Y_SAND, 0.2, Math.PI / 2);
    this._place(P.makeBarrel(this.mats, {}), 8.0, Y_SAND, 0.4, 0);
    this._place(P.makeBarrel(this.mats, { open: true }), 8.5, Y_SAND, -0.6, 0.4);

    // carro queimado, entulho e pneus
    this._place(P.makeVehicle(this.mats, { kind: 'sedan', burned: true, doorOpen: true }),
      5.6, Y_SAND, -13.4, 0.62);
    this._place(P.makeRubblePile(this.mats, { r: 2.0, h: 0.6, rng: P.makeRng(65), count: 26 }),
      -7.2, Y_SAND, -6.0, 0);
    this._place(P.makeRubblePile(this.mats, { r: 1.4, h: 0.45, rng: P.makeRng(66), count: 16 }),
      13.4, Y_SAND, 12.0, 0);
    for (let i = 0; i < 5; i++) {
      this._place(P.makeTire(this.mats, {}), 13.2 + (i % 2) * 0.16, Y_SAND + i * 0.22, -6.0, i * 0.7);
    }
    this._place(P.makeDumpster(this.mats, {}), -7.4, Y_SAND, 11.6, 0.5);
    this._place(P.makeTarpStack(this.mats, { count: 4, rng: P.makeRng(67) }), 13.0, Y_SAND, -16.0, -0.3);

    /**
     * VARAIS. Os dois anteriores terminavam no ar: o de 11,5 m parava a 0,8 m
     * da fachada da F1 e o de 9,0 m a 0,8 m da E1, entao o que sobrava na tela
     * era pano boiando. Cada varal agora ATRAVESSA um vao real, de parede a
     * parede, e a propria funcao ja poe bucha de fixacao nas duas pontas.
     */
    // passagem norte: da fachada leste da C3B (x -9) a fachada oeste da F1 (x -2)
    this._place(P.makeLaundryLine(this.mats, { len: 7.1, rng: P.makeRng(68) }), -5.5, 5.2, 17.6, 0);
    this._place(P.makeLaundryLine(this.mats, { len: 7.1, rng: P.makeRng(70) }), -5.5, 6.3, 28.4, 0);
    // beco sul: da fachada norte da C1 (z -16) a fachada sul da C2 (z -12,5)
    this._place(P.makeLaundryLine(this.mats, { len: 3.6, rng: P.makeRng(69) }),
      -20.0, 3.9, -14.25, Math.PI / 2);
    this._place(P.makeLaundryLine(this.mats, { len: 3.6, rng: P.makeRng(74) }),
      -26.5, 4.3, -14.25, Math.PI / 2);
    // beco norte: da fachada norte da C2 (z 8) a fachada sul da C3A (z 11,5)
    this._place(P.makeLaundryLine(this.mats, { len: 3.6, rng: P.makeRng(75) }),
      -14.0, 3.85, 9.75, Math.PI / 2);

    /**
     * [duna] O chao encontrava a alvenaria numa aresta de 90 graus perfeita em
     * todo o patio. Areia soprada acumulada nas bases mata a aresta e da volume
     * ao que era uma chapa lisa com textura repetida.
     */
    const duneRng = P.makeRng(6060);
    const dunes = [
      [-8.2, -14.0], [-8.4, -4.0], [-8.3, 6.5], [-8.2, 12.6],
      [1.0, -17.2], [8.6, -17.3], [-3.4, 14.2], [7.4, 14.3],
      [14.2, -8.0], [14.3, 2.4], [14.1, 10.5], [11.0, -17.2]
    ];
    for (let i = 0; i < dunes.length; i++) {
      this._place(P.makeRubblePile(this.mats, {
        mat: 'sand', r: 1.7 + duneRng() * 1.3, h: 0.11 + duneRng() * 0.09,
        rng: P.makeRng(6100 + i), count: 0
      }), dunes[i][0], Y_SAND - 0.03, dunes[i][1], duneRng() * 3.1);
    }

    /**
     * [vegetacao] Nao havia uma unica silhueta organica no mapa inteiro: nem
     * arvore, nem arbusto, nem trepadeira em 8100 m2, e cada quadro lia como
     * maquete. Duas palmeiras ao lado da fonte dao a curva que faltava.
     */
    this._place(P.makePalm(this.mats, { h: 5.6, rng: P.makeRng(801) }), 4.9, Y_SAND, -6.4, 0.6);
    this._place(P.makePalm(this.mats, { h: 4.7, rng: P.makeRng(802) }), -0.6, Y_SAND, -8.6, -1.1);
    this._place(P.makePalm(this.mats, { h: 6.2, rng: P.makeRng(803) }), 12.4, Y_SAND, 8.6, 2.2);
  }

  // -------------------------------------------------------------------------
  // 9. beco leste
  // -------------------------------------------------------------------------

  _buildEastAlley() {
    const b = this.batch;
    b.setRegion('leste');
    const rng = P.makeRng(701);

    this._place(P.makeDumpster(this.mats, {}), 35.4, Y_GRAV, -24.0, 0.06);
    this._place(P.makeDumpster(this.mats, {}), 35.6, Y_GRAV, 18.0, -0.1);
    this._place(P.makeVehicle(this.mats, { kind: 'pickup', doorOpen: false }),
      37.2, Y_GRAV, -13.5, Math.PI / 2 + 0.05);
    // encostado no lado da arcada: estreita o beco sem fechar a passagem
    this._place(P.makeContainer(this.mats, { len: 6.06 }), 39.1, Y_GRAV, 27.5, Math.PI / 2 + 0.03);

    // O par tambor+caixote a cada 8,6 m, sempre com o mesmo offset de 2,4 m,
    // dava um ritmo de metronomo ao beco inteiro. Agora a posicao sacode, uma
    // iteracao em tres e pulada e o prop e sorteado entre cinco.
    for (let i = 0; i < 10; i++) {
      const z = -30 + i * 6.4 + P.rand(rng, -2.6, 2.6);
      if (z < -31 || z > 30) continue;
      if (rng() < 0.3) continue;
      const px = 34.7 + rng() * 2.0;
      const pick = Math.floor(rng() * 5);
      if (pick === 0) {
        this._place(P.makeBarrel(this.mats, { mat: 'metal', open: rng() > 0.6,
          variant: rng() > 0.55 ? (rng() > 0.5 ? 1 : 2) : 0 }), px, Y_GRAV, z, rng() * 3.1);
      } else if (pick === 1) {
        const cw = 0.72 + rng() * 0.34;
        const ch = 0.58 + rng() * 0.3;
        this._place(P.makeCrate(this.mats, { w: cw, h: ch, d: cw,
          variant: Math.floor(rng() * 4) }), px, Y_GRAV, z, rng() * 3.1);
        if (rng() > 0.7) {
          this._place(P.makeCrate(this.mats, { w: cw * 0.84, h: ch * 0.8, d: cw * 0.84,
            variant: Math.floor(rng() * 4) }), px + 0.06, Y_GRAV + ch, z - 0.05, rng() * 3.1);
        }
      } else if (pick === 2) {
        const n = 2 + Math.floor(rng() * 3);
        for (let k = 0; k < n; k++) {
          this._place(P.makeTire(this.mats, {}), px, Y_GRAV + k * 0.22, z, k * 0.9);
        }
      } else if (pick === 3) {
        this._place(P.makeRubblePile(this.mats, { r: 1.0 + rng() * 0.8, h: 0.32 + rng() * 0.28,
          rng: P.makeRng(710 + i), count: 14 }), px + 0.4, Y_GRAV, z, 0);
      }
    }
    /**
     * VARAIS DO BECO. Antes eram colocados com rotY = PI/2 e len 6,4 dentro de
     * um beco de 7 m: corriam PARALELOS as duas paredes, centrados, com as duas
     * pontas a mais de 3 m de qualquer alvenaria. Agora atravessam o vao (rotY
     * 0, len 6,9, de x 34,05 a 40,95) e as alturas ficam na faixa em que existe
     * parede dos DOIS lados - do lado leste quem segura e a platibanda do
     * terraco da arcada, que vai de 3,85 a 4,77 m.
     */
    const lineZ = [-26.0, -18.5, -9.0, -2.0, 18.5];
    for (let i = 0; i < lineZ.length; i++) {
      const line = P.makeLaundryLine(this.mats, { len: 6.9, rng: P.makeRng(75 + i) });
      this._place(line, 37.5, 4.05 + (i % 2) * 0.42, lineZ[i], 0);
    }
    // uma palmeira no beco, contra a colunata
    this._place(P.makePalm(this.mats, { h: 4.4, rng: P.makeRng(804) }), 35.6, Y_GRAV, 14.6, 1.4);
    // passagem leste (corredor de 3 m entre o patio e o beco)
    b.setRegion('centro');
    this._place(P.makeCrate(this.mats, { w: 0.8, h: 0.7, d: 0.8 }), 20.0, Y_SAND, 13.4, 0.3);
    this._place(P.makeBarrel(this.mats, {}), 27.5, Y_SAND, 13.7, 0);
    this._place(P.makeAwning(this.mats, { w: 3.0, d: 1.5, drop: 0.5 }), 24.0, 3.2, 12.05, 0);
  }

  // -------------------------------------------------------------------------
  // 10. travessa sul e rua norte
  // -------------------------------------------------------------------------

  _buildCrossStreets() {
    const b = this.batch;
    b.setRegion('borda');
    const rng = P.makeRng(801);

    // --- travessa sul ------------------------------------------------------
    for (let i = 0; i < 5; i++) {
      this._place(P.makePole(this.mats, { h: 8.0, transformer: i === 1 }),
        -34 + i * 18, Y_CALC, -33.2, Math.PI / 2);
    }
    this._place(P.makeContainer(this.mats, { len: 6.06 }), -8.0, Y_ASP, -40.2, 0.03);
    this._place(P.makeContainer(this.mats, { len: 6.06, mat: 'metal' }), 1.2, Y_ASP, -40.4, -0.05);
    this._place(P.makeVehicle(this.mats, { kind: 'sedan', burned: true }), 18.0, Y_ASP, -38.4, 0.2);
    for (let i = 0; i < 5; i++) {
      this._place(P.makeJerseyBarrier(this.mats, { len: 2.4 }), 24 + i * 2.5, Y_ASP, -36.6 + (i % 2) * 0.9,
        (i % 2) * -0.1);
    }
    this._place(P.makeSandbagWall(this.mats, { len: 5.4, rows: 4, rng: P.makeRng(81) }),
      -20.0, Y_ASP, -37.6, 0.03);
    this._place(P.makeSandbagWall(this.mats, { len: 3.0, rows: 4, rng: P.makeRng(82) }),
      -23.2, Y_ASP, -39.2, Math.PI / 2);
    this._place(P.makeRubblePile(this.mats, { r: 2.6, h: 0.8, rng: P.makeRng(83), count: 32 }),
      33.0, Y_ASP, -43.0, 0);
    this._place(P.makeDumpster(this.mats, {}), -30.0, Y_CALC, -33.4, Math.PI);
    for (let i = 0; i < 4; i++) {
      this._place(P.makeBarrel(this.mats, { open: i === 1 }), -12.5 + i * 0.9, Y_ASP, -43.4 + (i % 2) * 0.8,
        rng() * 3);
    }

    // --- rua norte ---------------------------------------------------------
    for (let i = 0; i < 6; i++) {
      this._place(P.makePole(this.mats, { h: 8.4, transformer: i === 3 }),
        -40 + i * 17, Y_CALC, 33.4, -Math.PI / 2);
    }
    this._place(P.makeVehicle(this.mats, { kind: 'sedan', doorOpen: true }),
      -11.0, Y_ASP, 40.0, Math.PI + 0.06);
    this._place(P.makeVehicle(this.mats, { kind: 'pickup', burned: true }),
      14.5, Y_ASP, 36.6, -0.14);
    for (let i = 0; i < 6; i++) {
      this._place(P.makeJerseyBarrier(this.mats, { len: 2.4 }), -25 + i * 2.5, Y_ASP, 38.2 + (i % 2) * 1.0,
        (i % 2) * 0.08);
    }
    this._place(P.makeSandbagWall(this.mats, { len: 4.6, rows: 4, rng: P.makeRng(84) }),
      26.0, Y_ASP, 37.0, 0.04);
    this._place(P.makeContainer(this.mats, { len: 6.06 }), 33.5, Y_ASP, 41.0, Math.PI / 2 + 0.02);
    this._place(P.makeDumpster(this.mats, {}), 5.0, Y_CALC, 43.4, 0.2);
    this._place(P.makeRubblePile(this.mats, { r: 2.2, h: 0.7, rng: P.makeRng(85), count: 26 }),
      -34.0, Y_CALC, 43.0, 0);
    for (let i = 0; i < 4; i++) {
      this._place(P.makeCrate(this.mats, { w: 0.9, h: 0.75, d: 0.9 }),
        -20 + i * 1.1, Y_CALC, 32.6, rng() * 3.1);
    }
    this._place(P.makePlanter(this.mats, { w: 1.8, rng: P.makeRng(86) }), 20.0, Y_CALC, 32.8, 0);
  }

  // -------------------------------------------------------------------------
  // 11. interiores (armazem e garagem)
  // -------------------------------------------------------------------------

  _buildInteriors() {
    const b = this.batch;
    b.setRegion('centro');

    // --- armazem (dentro da C1) -------------------------------------------
    // Piso de CONCRETO, nao a ceramica de 1,1 m das calcadas: modulo de piso
    // publico dentro de um galpao era o que mais entregava "interior generico".
    this._floor('concrete', RUA_E + 0.4, -31.6, -9.4, -16.4, Y_ARMAZEM, 0.3);
    // poeira acumulada rente as quatro paredes - e o que separa chao de galpao
    // de calcada varrida
    this._interiorDust(RUA_E + 0.4, -31.6, -9.4, -16.4, Y_ARMAZEM, 1.25);
    // vigas do forro: sem elas a laje de 4,9 m le como tampa lisa
    for (let i = 0; i < 7; i++) {
      const bx = -30.4 + i * 3.1;
      this._slab('concrete', bx, 4.62, -31.6, bx + 0.32, 4.9, -16.4, { solid: false });
    }
    for (let i = 0; i < 4; i++) {
      const bz = -30.0 + i * 4.0;
      this._slab('wood', RUA_E + 0.4, 4.42, bz, -9.4, 4.62, bz + 0.2, { solid: false });
    }
    // pilares estruturais quebram a visao dentro do galpao
    for (let i = 0; i < 4; i++) {
      const px = -27.5 + i * 5.6;
      this._slab('concrete', px - 0.26, Y_ARMAZEM, -25.2, px + 0.26, 4.95, -24.68);
      this._slab('concrete', px - 0.36, 4.6, -25.3, px + 0.36, 4.95, -24.58, { solid: false });
    }
    // Parede divisoria com um vao de porta: cria o comodo dentro do comodo e
    // corta a visada de ponta a ponta. Ela NAO chega no fim do galpao (para no
    // z -23,2), entao sempre existe a volta por fora - nada de beco sem saida.
    const rects = splitRects(-31.6, -23.2, Y_ARMAZEM - 0.4, 4.9,
      [{ u0: -29.5, u1: -28.0, v0: 0, v1: 2.3 }]);
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      this._slab('brick', -20.6, r.v0, r.u0, -20.2, r.v1, r.u1);
    }
    this._place(P.makeShelf(this.mats, { w: 2.4, h: 2.2 }), -29.2, Y_ARMAZEM, -30.2, 0);
    this._place(P.makeShelf(this.mats, { w: 2.4, h: 2.2 }), -26.4, Y_ARMAZEM, -30.2, 0);
    this._place(P.makeShelf(this.mats, { w: 2.2, h: 2.0 }), -11.2, Y_ARMAZEM, -22.0, Math.PI / 2);
    this._place(P.makeWorkbench(this.mats, { w: 2.4 }), -13.0, Y_ARMAZEM, -29.6, Math.PI);
    for (let i = 0; i < 5; i++) {
      this._place(P.makePallet(this.mats), -25.0 + i * 3.0, Y_ARMAZEM, -19.4, (i % 2) * 0.2);
    }
    /**
     * Povoamento ENCOSTADO NA PAREDE. O sorteio uniforme sobre o retangulo
     * inteiro espalhava tudo pelo meio e nada tocava alvenaria: o miolo ficava
     * vazio e o galpao lia como caixa com objetos jogados dentro. Aqui as
     * pilhas nascem numa faixa de 1,4 m a partir de cada face interna, e so 4
     * ficam no miolo, de proposito, como cobertura.
     */
    const rng = P.makeRng(901);
    const wallSpots = this._wallSpots(RUA_E + 1.1, -30.9, -10.1, -17.1, 1.4, 14, rng);
    for (let i = 0; i < wallSpots.length; i++) {
      const sp = wallSpots[i];
      if (rng() > 0.72) {
        this._place(P.makeBarrel(this.mats, { open: rng() > 0.65,
          variant: rng() > 0.6 ? 1 : 0 }), sp[0], Y_ARMAZEM, sp[1], rng() * 3.1);
        continue;
      }
      const cw = 0.78 + rng() * 0.35;
      const ch = 0.6 + rng() * 0.3;
      this._place(P.makeCrate(this.mats, { w: cw, h: ch, d: 0.82,
        variant: Math.floor(rng() * 4) }), sp[0], Y_ARMAZEM, sp[1], sp[2] + P.rand(rng, -0.3, 0.3));
      if (rng() > 0.62) {
        this._place(P.makeCrate(this.mats, { w: cw * 0.88, h: ch * 0.84, d: 0.72,
          variant: Math.floor(rng() * 4) }), sp[0] + 0.06, Y_ARMAZEM + ch, sp[1] - 0.05,
        rng() * 3.1);
      }
    }
    // as 4 pilhas do miolo: cobertura de verdade no meio do galpao
    const core = [[-24.6, -21.6], [-18.4, -26.4], [-13.6, -21.0], [-27.2, -27.0]];
    for (let i = 0; i < core.length; i++) {
      const ch = 0.66 + rng() * 0.22;
      this._place(P.makeCrate(this.mats, { w: 0.92, h: ch, d: 0.92,
        variant: Math.floor(rng() * 4) }), core[i][0], Y_ARMAZEM, core[i][1], rng() * 3.1);
      this._place(P.makeCrate(this.mats, { w: 0.8, h: 0.62, d: 0.8,
        variant: Math.floor(rng() * 4) }), core[i][0] + 0.07, Y_ARMAZEM + ch, core[i][1] + 0.05,
      rng() * 3.1);
    }
    this._place(P.makeContainer(this.mats, { len: 6.06 }), -15.0, Y_ARMAZEM, -27.5, 0.02);
    for (let i = 0; i < 3; i++) {
      this._place(P.makeHangingLamp(this.mats, { len: 1.1 }), -26 + i * 7.0, 4.85, -24.0, 0);
    }
    this._place(P.makeTarpStack(this.mats, { count: 3, rng: P.makeRng(91) }), -22.5, Y_ARMAZEM, -30.0, 0.4);

    // --- garagem / oficina (dentro da D1) ---------------------------------
    b.setRegion('leste');
    this._floor('concrete', 15.4, -31.6, 33.6, -18.4, Y_GARAGEM, 0.3);
    this._slab('concrete', 15.4, 3.6, -31.6, 33.6, 3.95, -18.4);   // forro da garagem
    // grelha de vigas: 18,2 x 13,2 m de laje lisa de ponta a ponta era o que
    // fazia o teto da garagem ler como tampa, nao como estrutura
    for (let i = 0; i < 6; i++) {
      const bx = 17.0 + i * 3.0;
      this._slab('concrete', bx, 3.32, -31.6, bx + 0.34, 3.6, -18.4, { solid: false });
    }
    for (let i = 0; i < 4; i++) {
      const bz = -29.8 + i * 3.4;
      this._slab('concrete', 15.4, 3.16, bz, 33.6, 3.32, bz + 0.26, { solid: false });
    }
    this._interiorDust(15.4, -31.6, 33.6, -18.4, Y_GARAGEM, 1.15);
    for (let i = 0; i < 3; i++) {
      const px = 20.0 + i * 6.4;
      this._slab('metal', px - 0.16, Y_GARAGEM, -25.2, px + 0.16, 3.6, -24.88);
    }
    this._place(P.makeVehicle(this.mats, { kind: 'sedan', doorOpen: true, flatTire: false }),
      24.5, Y_GARAGEM, -27.0, 0.06);
    this._place(P.makeWorkbench(this.mats, { w: 2.6 }), 19.0, Y_GARAGEM, -19.4, Math.PI);
    this._place(P.makeWorkbench(this.mats, { w: 2.2 }), 30.4, Y_GARAGEM, -19.4, Math.PI);
    this._place(P.makeShelf(this.mats, { w: 2.2, h: 2.0, d: 0.55 }), 33.0, Y_GARAGEM, -28.0, Math.PI / 2);
    for (let i = 0; i < 8; i++) {
      this._place(P.makeTire(this.mats, {}), 32.4 - (i % 2) * 1.0, Y_GARAGEM + Math.floor(i / 2) * 0.22,
        -22.5, i * 0.6);
    }
    for (let i = 0; i < 5; i++) {
      this._place(P.makeBarrel(this.mats, { mat: 'metal' }), 17.2 + (i % 3) * 0.75,
        Y_GARAGEM, -29.6 + Math.floor(i / 3) * 0.8, 0);
    }
    for (let i = 0; i < 6; i++) {
      this._place(P.makeCrate(this.mats, { w: 0.75, h: 0.65, d: 0.75, variant: i % 4 }),
        27.0 + (i % 3) * 0.8, Y_GARAGEM + Math.floor(i / 3) * 0.65, -30.4, 0.2 * i);
    }
    // encosto de parede: bancada de fundo, tambores e pneus contra a alvenaria
    const gRng = P.makeRng(902);
    const gSpots = this._wallSpots(16.5, -30.7, 32.5, -19.3, 1.3, 9, gRng);
    for (let i = 0; i < gSpots.length; i++) {
      const sp = gSpots[i];
      const pick = Math.floor(gRng() * 3);
      if (pick === 0) {
        this._place(P.makeBarrel(this.mats, { mat: 'metal', variant: gRng() > 0.5 ? 1 : 2 }),
          sp[0], Y_GARAGEM, sp[1], gRng() * 3.1);
      } else if (pick === 1) {
        this._place(P.makeCrate(this.mats, { w: 0.8, h: 0.66, d: 0.8,
          variant: Math.floor(gRng() * 4) }), sp[0], Y_GARAGEM, sp[1], sp[2]);
      } else {
        const n = 2 + Math.floor(gRng() * 3);
        for (let k = 0; k < n; k++) {
          this._place(P.makeTire(this.mats, {}), sp[0], Y_GARAGEM + k * 0.22, sp[1], k * 0.8);
        }
      }
    }
    for (let i = 0; i < 3; i++) {
      this._place(P.makeHangingLamp(this.mats, { len: 0.9 }), 20 + i * 5.5, 3.55, -25.0, 0);
    }
    this._place(P.makeJerseyBarrier(this.mats, { len: 2.4 }), 30.0, Y_GARAGEM, -24.0, Math.PI / 2);
  }

  // -------------------------------------------------------------------------
  // 12. fiacao aerea
  // -------------------------------------------------------------------------

  _buildWires() {
    const b = this.batch;
    b.setRegion('oeste');
    const poles = this._poles || [];
    for (let i = 0; i < poles.length - 1; i++) {
      const a = poles[i];
      const c = poles[i + 1];
      for (let k = 0; k < 3; k++) {
        const off = -0.7 + k * 0.55;
        this.batch.addObject(P.makeWire(this.mats,
          a[0], a[1] + 0.14, a[2] + off, c[0], c[1] + 0.14, c[2] + off, 0.75, 0.02));
      }
      // ramal ate a fachada
      this.batch.addObject(P.makeWire(this.mats,
        a[0], a[1] - 0.6, a[2], RUA_E - 0.1, 4.6, a[2] + 1.6, 0.5, 0.016));
      this.batch.addObject(P.makeWire(this.mats,
        a[0], a[1] - 0.9, a[2], RUA_W + 0.1, 5.4, a[2] - 2.2, 0.6, 0.016));
    }

    // fios atravessando o patio e o beco (camadas contra o ceu)
    b.setRegion('centro');
    this.batch.addObject(P.makeWire(this.mats, -9.1, 5.6, -6.0, 15.1, 7.2, -8.0, 0.9, 0.02));
    this.batch.addObject(P.makeWire(this.mats, -9.1, 5.2, 3.0, 15.1, 6.6, 1.0, 0.85, 0.018));
    this.batch.addObject(P.makeWire(this.mats, -2.1, 8.6, 18.0, -9.1, 9.8, 26.0, 0.7, 0.018));
    b.setRegion('leste');
    this.batch.addObject(P.makeWire(this.mats, 34.1, 6.6, -22.0, 44.9, 8.4, -18.0, 0.8, 0.02));
    this.batch.addObject(P.makeWire(this.mats, 34.1, 5.4, 20.0, 44.9, 7.0, 24.0, 0.8, 0.02));
    this.batch.addObject(P.makeWire(this.mats, 34.1, 7.4, 0.0, 44.9, 6.2, 6.0, 0.7, 0.018));
  }

  // -------------------------------------------------------------------------
  // 13. entulho em InstancedMesh (decoracao pura, sem colisao)
  // -------------------------------------------------------------------------

  _buildDebrisInstances() {
    const zones = [
      // [x0, z0, x1, z1, densidade]
      [-44.5, -34, -31.5, 34, 1.0],     // rua principal
      [-9, -18, 15, 15, 0.9],           // patio
      [34, -32, 45, 31, 1.2],           // beco leste
      [-45, 31, 45, 45, 0.7],           // rua norte
      [-45, -45, 45, -32, 0.7],         // travessa sul
      [-31, -16, -9, -12.5, 1.4],       // beco sul
      [-31, 8, -9, 11.5, 1.4],          // beco norte
      [34, -30, 35.2, 30, 2.0],         // pe da parede oeste do beco leste
      [39.9, -30, 41, 22, 2.0]          // pe da colunata da arcada
    ];
    const defs = [
      { geo: P.brickChunkGeometry(), mat: 'brick', count: 500, scale: [0.7, 1.5] },
      { geo: P.slabChunkGeometry(), mat: 'concrete', count: 380, scale: [0.5, 1.3] },
      { geo: P.slabChunkGeometry(), mat: 'concrete', count: 90, scale: [2.0, 3.6], cast: true },
      { geo: new THREE.CylinderGeometry(0.035, 0.04, 0.11, 7), mat: 'metal_thin', count: 170, scale: [0.7, 1.2] }
    ];
    const rng = P.makeRng(4242);
    const m4 = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const pos = new THREE.Vector3();
    const scl = new THREE.Vector3();

    for (let d = 0; d < defs.length; d++) {
      const def = defs[d];
      const mesh = new THREE.InstancedMesh(def.geo, this.mats.get(def.mat), def.count);
      mesh.name = 'lvl_entulho_' + def.mat;
      mesh.userData.surface = def.mat;
      // so as lascas GRANDES projetam sombra: um caco de 10 cm nao paga o custo
      // de fill no shadow map, mas uma laje de 80 cm sem sombra de contato boia
      mesh.castShadow = !!def.cast;
      mesh.receiveShadow = true;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      let placed = 0;
      let guard = 0;
      while (placed < def.count && guard < def.count * 12) {
        guard++;
        const zi = Math.floor(rng() * zones.length);
        const z = zones[zi];
        if (rng() > z[4] * 0.85) continue;
        let px = z[0] + rng() * (z[2] - z[0]);
        let pz = z[1] + rng() * (z[3] - z[1]);
        // 60% do entulho GRUDA na borda mais proxima da zona: detrito nao cai
        // uniforme, ele encosta na parede e no meio-fio. Distribuicao uniforme
        // no retangulo inteiro le como confete, nao como abandono.
        let edge = 1;
        if (rng() < 0.6) {
          const dx = Math.min(px - z[0], z[2] - px);
          const dz = Math.min(pz - z[1], z[3] - pz);
          const k = 0.12 + rng() * 0.42;
          if (dx < dz) px = (px - z[0] < z[2] - px) ? z[0] + k : z[2] - k;
          else pz = (pz - z[1] < z[3] - pz) ? z[1] + k : z[3] - k;
          edge = 1.45;
        }
        const gy = this._groundY(px, pz);
        const s = (def.scale[0] + rng() * (def.scale[1] - def.scale[0])) * edge;
        pos.set(px, gy + 0.015 * s, pz);
        e.set((rng() - 0.5) * 0.5, rng() * 6.28, (rng() - 0.5) * 0.5);
        q.setFromEuler(e);
        scl.set(s, s * (0.7 + rng() * 0.5), s);
        m4.compose(pos, q, scl);
        mesh.setMatrixAt(placed, m4);
        placed++;
      }
      // sobra (se houver) sai de cena, encolhida na origem
      for (let i = placed; i < def.count; i++) {
        m4.makeScale(0.0001, 0.0001, 0.0001);
        mesh.setMatrixAt(i, m4);
      }
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
      this.root.add(mesh);
    }
  }

  // -------------------------------------------------------------------------
  // 14. spawns e pontos de vista
  // -------------------------------------------------------------------------

  _spawn(x, z, yOverride) {
    const y = yOverride !== undefined ? yOverride : this._groundY(x, z) + 0.08;
    return new THREE.Vector3(x, y, z);
  }

  /**
   * Jogador nasce no sul/oeste, inimigo no norte/leste. A distancia minima entre
   * os dois conjuntos passa de 30 m, entao ninguem aparece na cara do outro.
   */
  _makeSpawnPoints() {
    this.spawnPoints.player = [
      this._spawn(-38.0, -37.6),     // rua principal, boca sul
      this._spawn(-3.0, -38.4),      // travessa sul, meio
      this._spawn(-16.0, -20.0),     // dentro do armazem
      this._spawn(-38.4, -14.5),     // rua principal, meio
      this._spawn(-6.5, -14.5),      // patio, canto sudoeste
      this._spawn(24.0, -25.5),      // dentro da garagem
      this._spawn(12.0, -15.0),      // patio, canto sudeste
      this._spawn(37.5, -22.0),      // beco leste, boca sul
      this._spawn(-29.0, -14.2)      // beco sul
    ];
    this.spawnPoints.enemy = [
      this._spawn(-38.0, 38.6),      // rua norte, oeste
      this._spawn(-2.0, 38.8),       // rua norte, centro
      this._spawn(30.0, 38.2),       // rua norte, leste
      this._spawn(37.5, 24.0),       // beco leste, norte
      this._spawn(-5.5, 24.0),       // passagem norte
      this._spawn(24.5, 13.5),       // passagem leste
      this._spawn(-38.4, 22.0),      // rua principal, norte
      this._spawn(4.0, 11.0),        // patio, borda norte
      this._spawn(-20.0, 14.5, H_C3A + 0.08),   // laje da C3A
      this._spawn(25.0, 8.0, H_D2A + 0.08),     // laje da D2A
      this._spawn(43.0, 10.0, ARC_TOP + 0.08)   // terraco da arcada
    ];
  }

  /** Yaw/pitch em GRAUS a partir de um par (de onde, para onde). */
  _look(fx, fy, fz, tx, ty, tz) {
    const dx = tx - fx;
    const dy = ty - fy;
    const dz = tz - fz;
    const yaw = (Math.atan2(-dx, -dz) * 180) / Math.PI;
    const pitch = (Math.atan2(dy, Math.hypot(dx, dz)) * 180) / Math.PI;
    return { yaw: Math.round(yaw * 10) / 10, pitch: Math.round(pitch * 10) / 10 };
  }

  _vp(name, fx, fy, fz, tx, ty, tz) {
    const a = this._look(fx, fy, fz, tx, ty, tz);
    this.viewpoints.push({ name, position: [fx, fy, fz], yaw: a.yaw, pitch: a.pitch });
  }

  /**
   * Pontos de vista da revisao visual. Cobrem: rua longa, patio, beco fechado,
   * laje, dois interiores, contraluz nos dois sentidos, um close de parede para
   * julgar textura e dois planos gerais.
   */
  _makeViewpoints() {
    const eye = 1.65;
    this.viewpoints.length = 0;

    // 1. rua principal olhando para o norte - a linha de tiro longa
    this._vp('rua_principal_norte', -37.6, Y_ASP + eye, -30.0, -37.0, 2.6, 34.0);
    // 2. rua principal olhando para o sul, com o caminhao em primeiro plano
    this._vp('rua_principal_sul', -37.0, Y_ASP + eye, 16.0, -38.4, 1.2, -22.0);
    // 3. patio, olhando na diagonal por cima do muro baixo e das barracas
    this._vp('patio_geral', -7.0, Y_SAND + eye, -9.0, 12.0, 1.2, 8.0);
    // 4. laje da C2 dominando o patio
    this._vp('laje_c2', -11.5, H_C2 + eye, 1.0, 8.0, 0.6, -8.0);
    // 4b. patio olhando para a escada externa e a laje que domina tudo
    this._vp('patio_escada', 13.5, Y_SAND + eye, 3.0, -9.5, 3.2, -3.0);
    // 5. passarela sobre o beco norte
    this._vp('passarela_beco', -20.0, H_C2 + eye + 0.05, 9.9, -40.0, 2.2, 9.4);
    // 6. beco norte no nivel do chao, contra a rua
    this._vp('beco_estreito', -12.0, Y_GRAV + eye, 9.7, -40.0, 1.4, 9.7);
    // 7. interior do armazem, olhando na diagonal para as laminas de sol
    this._vp('armazem_interior', -13.0, Y_ARMAZEM + eye, -17.6, -27.0, 0.6, -27.0);
    // 8. interior da garagem, olhando para o portao
    this._vp('garagem_interior', 28.0, Y_GARAGEM + eye, -26.0, 15.0, 1.6, -25.0);
    // 9. arcada leste - colunata e sombras compridas
    this._vp('arcada_leste', 43.0, Y_GRAV + eye, -18.0, 43.0, 1.4, 20.0);
    // 10. beco leste do nivel do chao, olhando para a rampa de entulho
    this._vp('beco_leste', 37.5, Y_GRAV + eye, -6.0, 35.0, 3.6, 9.0);
    // 11. contraluz para oeste na rua norte (sol baixo no fim de tarde)
    this._vp('contraluz_oeste', 6.0, Y_ASP + eye, 38.4, -44.0, 6.0, 38.0);
    // 12. contraluz para leste na travessa sul
    this._vp('contraluz_leste', -8.0, Y_ASP + eye, -38.4, 44.0, 6.0, -38.0);
    // 13. close de parede (1,2 m) para julgar textura, escala de texel e reboco
    this._vp('parede_close', -32.2, 1.5, -28.5, -31.0, 1.35, -28.5);
    // 14. plano geral do quarteirao (canto sudoeste, alto)
    this._vp('plano_geral', -41.0, 19.0, -41.0, 6.0, 1.0, 12.0);
    // 15. plano geral do lado oposto
    this._vp('plano_geral_norte', 41.0, 17.5, 41.0, -6.0, 1.0, -10.0);
    // 16. terraco da arcada correndo para o sul (a rota alta sobre o beco).
    // A mira passa raspando por dentro da platibanda: mirar na diagonal aqui
    // enche metade do quadro com o proprio parapeito.
    this._vp('terraco_arcada', 43.0, ARC_TOP + eye, 18.0, 42.4, 1.2, -22.0);
  }
}
